import { useState, useMemo, useEffect, useCallback } from "react";
import WineDashboardStats from "./WineDashboardStats";
import BeerDashboardStats from "./BeerDashboardStats";
import { mapBeerAnalyticsToStats } from "../../utils/beerAnalytics";
import { MenuService } from "../../services/MenuService";
import AllergenReferenceMenu from "./allergen-reference-menu";
import AllergenAccommodationMatrix from "./allergen-accommodation-matrix";
import FoodBulkActionModal from "./FoodBulkActionModal";
import WineBulkActionModal from "./WineBulkActionModal";
import BeerBulkActionModal from "./BeerBulkActionModal";
import BeerImage from "./BeerImage";
import Dropdown from "./dropdown";
import WineReferenceSheet from "./WineReferenceSheet";
import BeerReferenceSheet from "./BeerReferenceSheet";
import {
  getFermentationFamily,
  getOriginClassification,
  getAbvCategory,
  getIbuCategory,
  getGroupBadgeColor as getBeerCategoryColor,
  getLevelBadgeColor as getBeerLevelColor,
} from "../../utils/beerTaxonomy";
export default function MenuItemsTable({
  items,
  onEditItem,
  activeTab = "food",
  previousTable,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  onPageChange,
  loading = false,
  itemsPerPage = 10,
  onItemsPerPageChange = () => { },
  restaurantUuid = null,
  restaurantName,
  validDishTypes,
  sortBy = null,
  sortDirection = "asc",
  onSortChange,
  onRefresh,
  onFetchAllItems,
}) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [restoreItem, setRestoreItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [bulkRestoreLoading, setBulkRestoreLoading] = useState(false);
  const [showBulkRestoreModal, setShowBulkRestoreModal] = useState(false);
  const [bulkRestoreDraftLoading, setBulkRestoreDraftLoading] = useState(false);
  const [showBulkRestoreDraftModal, setShowBulkRestoreDraftModal] =
    useState(false);
  const [showFoodBulkModal, setShowFoodBulkModal] = useState(false);
  const [foodBulkLoading, setFoodBulkLoading] = useState(false);
  const [showWineBulkModal, setShowWineBulkModal] = useState(false);
  const [wineBulkLoading, setWineBulkLoading] = useState(false);
  const [showBeerBulkModal, setShowBeerBulkModal] = useState(false);
  const [beerBulkLoading, setBeerBulkLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [showWineReferenceSheet, setShowWineReferenceSheet] = useState(false);
  const [showBeerReferenceSheet, setShowBeerReferenceSheet] = useState(false);
  const [dishAnalytics, setDishAnalytics] = useState(null);
  const [wineAnalyticsApi, setWineAnalyticsApi] = useState(null);
  const [beerAnalyticsApi, setBeerAnalyticsApi] = useState(null);

  // Paginated beer activity state (infinite scroll in the Beer dashboard)
  const [beerActivityData, setBeerActivityData] = useState([]);
  const [beerActivityPage, setBeerActivityPage] = useState(1);
  const [beerActivityLoading, setBeerActivityLoading] = useState(false);
  const [beerActivityHasMore, setBeerActivityHasMore] = useState(true);
  const [beerActivityTotalCount, setBeerActivityTotalCount] = useState(0);

  // Reset viewMode when tab changes
  useEffect(() => {
    setViewMode("table");
  }, [activeTab]);


  const wineStats = useMemo(() => {
    if (activeTab !== "wine" || !items) return null;

    const total = totalItems || items.length;
    const now = new Date();
    const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));

    // New Wines (30 Days)
    const newWines = items.filter(
      (i) => new Date(i.createdAt) > thirtyDaysAgo
    ).length;

    // Last Updated
    const lastUpdatedItem = items.reduce((latest, item) => {
      const itemDate = new Date(item.updatedAt);
      const latestDate = new Date(latest.updatedAt || 0);
      return itemDate > latestDate ? item : latest;
    }, {});

    // Last updated is now the name of the most recently updated item
    const lastUpdatedText = lastUpdatedItem.uuid
      ? `${lastUpdatedItem.producer_name || ""} ${lastUpdatedItem.product_name || lastUpdatedItem.name || ""
        }`.trim()
      : "";

    // Category Distribution
    const catCounts = {};
    items.forEach((i) => {
      const cat = i.category || "Other";
      catCounts[cat] = (catCounts[cat] || 0) + 1;
    });
    const wineCategory = Object.entries(catCounts)
      .map(([label, count]) => ({
        label,
        value: Math.round((count / total) * 100),
        count,
      }))
      .sort((a, b) => b.value - a.value);

    // Style Distribution
    const styleCounts = {};
    items.forEach((i) => {
      const s = i.sub_category || i.styleDisplay || "Unknown";
      styleCounts[s] = (styleCounts[s] || 0) + 1;
    });
    const styleDistribution = Object.entries(styleCounts)
      .map(([label, count]) => ({
        label,
        value: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Top Varietals
    const varCounts = {};
    items.forEach((i) => {
      (i.varietals || i.grape_varietals || []).forEach((v) => {
        varCounts[v] = (varCounts[v] || 0) + 1;
      });
    });
    const topVarietals = Object.entries(varCounts)
      .map(([label, count]) => ({
        label,
        value: Math.round((count / total) * 100),
        count,
      }))
      .sort((a, b) => b.value - a.value);

    // Regional
    const regCounts = {};
    items.forEach((i) => {
      const r = i.region?.country || "Unknown";
      regCounts[r] = (regCounts[r] || 0) + 1;
    });
    const regionalDistribution = Object.entries(regCounts)
      .map(([label, count]) => ({
        label,
        value: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Dietary
    const vegan = items.filter(
      (i) => i.is_vegan === true || i.vegan === "Yes"
    ).length;
    const organic = items.filter(
      (i) => i.is_organic === true || i.organic === "Yes"
    ).length;

    const dietaryConsiderations = [
      {
        label: "Vegan Friendly",
        value: `${Math.round((vegan / total) * 100)}%`,
      },
      { label: "Organic", value: `${Math.round((organic / total) * 100)}%` },
      { label: "Sustainability", value: "High" },
    ];

    // Recent Updates
    const recentUpdates = [...items]
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      .slice(0, 3)
      .map((i) => ({
        type: "update",
        text: `Updated pricing for ${i.product_name || i.name}`,
      }));

    return {
      total,
      newWines,
      removed: 3,
      lastUpdated: lastUpdatedText,
      wineCategory,
      styleDistribution,
      topVarietals,
      regionalDistribution,
      dietaryConsiderations,
      recentUpdates,
    };
  }, [items, activeTab]);

  const winePanelStats = useMemo(() => {
    if (wineAnalyticsApi) {
      const api = wineAnalyticsApi;
      const total = api.totalWines || wineStats?.total || items.length || 0;
      // Build category array either from API's array or from scalar percentage fields
      const categoryFromScalars = [
        {
          label: "Red",
          value: Number(api.redWinePercentage || 0),
          count: Math.round((Number(api.redWinePercentage || 0) / 100) * total),
        },
        {
          label: "White",
          value: Number(api.whiteWinePercentage || 0),
          count: Math.round(
            (Number(api.whiteWinePercentage || 0) / 100) * total
          ),
        },
        {
          label: "Rose",
          value: Number(api.roseWinePercentage || 0),
          count: Math.round(
            (Number(api.roseWinePercentage || 0) / 100) * total
          ),
        },
        {
          label: "Sparkling",
          value: Number(api.sparklingWinePercentage || 0),
          count: Math.round(
            (Number(api.sparklingWinePercentage || 0) / 100) * total
          ),
        },
      ].filter((c) => c.value > 0);

      const styleFromScalars = [
        {
          label: "Full-bodied Red",
          value: Number(api.fullBodiedRedPercentage || 0),
        },
        { label: "Crisp White", value: Number(api.crispWhite || 0) },
        { label: "Sweet / Dessert", value: Number(api.sweetDessert || 0) },
        { label: "Sparkling", value: Number(api.sparkling || 0) },
      ].filter((s) => s.value > 0);

      const totalForVar = total || 1;
      const topVarietalsFromScalars = [
        {
          label: "Cabernet Sauvignon",
          value: Math.round(
            (Number(api.cabernetSauvignon || 0) / totalForVar) * 100
          ),
        },
        {
          label: "Chardonnay",
          value: Math.round((Number(api.chardonnay || 0) / totalForVar) * 100),
        },
        {
          label: "Pinot Noir",
          value: Math.round((Number(api.pinotNoir || 0) / totalForVar) * 100),
        },
        {
          label: "Sauvignon Blanc",
          value: Math.round(
            (Number(api.sauvignonBlanc || 0) / totalForVar) * 100
          ),
        },
      ].filter((v) => v.value > 0);

      const regionalFromScalars = [
        {
          label: "France",
          value: Number(
            api.frnaceRegionPercentage || api.franceRegionPercentage || 0
          ),
        },
        { label: "Italy", value: Number(api.italyRegionPercentage || 0) },
        { label: "USA", value: Number(api.usaRegionPercentage || 0) },
        { label: "Other", value: Number(api.otherRegionPercentage || 0) },
      ].filter((r) => r.value > 0);

      const dietaryFromScalars = [
        {
          label: "Organic",
          value: total
            ? `${Math.round((Number(api.organicCount || api.organicWines || 0) / total) * 100)}%`
            : `${api.organicCount || api.organicWines || 0}`,
          count: api.organicCount || api.organicWines || 0,
        },
        {
          label: "Biodynamic",
          value: total
            ? `${Math.round((Number(api.biodynamicCount || api.biodynamicWines || 0) / total) * 100)}%`
            : `${api.biodynamicCount || api.biodynamicWines || 0}`,
          count: api.biodynamicCount || api.biodynamicWines || 0,
        },
        {
          label: "Vegan",
          value: total
            ? `${Math.round((Number(api.veganCount || api.veganWines || 0) / total) * 100)}%`
            : `${api.veganCount || api.veganWines || 0}`,
          count: api.veganCount || api.veganWines || 0,
        },
        {
          label: "Unfiltered",
          value: total
            ? `${((api.unfilteredCount / total) * 100).toFixed(1)}%`
            : '0%',
          count: api.unfilteredCount || 0,
        },
        {
          label: "Dry Wines",
          value: total
            ? `${((api.dryWinesCount / total) * 100).toFixed(1)}%`
            : '0%',
          count: api.dryWinesCount || 0,
        },
        {
          label: "Residual Sugar",
          value: total
            ? `${((api.residualSugarCount / total) * 100).toFixed(1)}%`
            : '0%',
          count: api.residualSugarCount || 0,
        },
      ].filter((d) => d.value && d.value !== "0%");

      // normalize API arrays/objects into consistent shapes for the dashboard
      const normalizedStyleDistribution =
        api.styleDistribution ||
        (styleFromScalars.length
          ? styleFromScalars
          : wineStats?.styleDistribution || {});

      const normalizedTopVarietals =
        api.topVarietals && Array.isArray(api.topVarietals)
          ? api.topVarietals.map((v) => ({
            label: v.name || v.label,
            value: Number(v.percentage || v.value || 0),
            count: v.count || 0,
          }))
          : topVarietalsFromScalars.length
            ? topVarietalsFromScalars
            : wineStats?.topVarietals || [];

      const normalizedDietary =
        dietaryFromScalars.length
          ? dietaryFromScalars
          : api.characteristics && typeof api.characteristics === "object"
            ? Object.keys(api.characteristics).map((k) => ({
              label: k,
              value: api.characteristics[k],
            }))
            : wineStats?.dietaryConsiderations || [];

      const normalizedRecent =
        api.recentWineUpdates && Array.isArray(api.recentWineUpdates)
          ? api.recentWineUpdates.map((u) => ({
            type: u.action,
            text: u.details?.description || u.details?.wine_uuid,
            user: u.user_uuid
              ? `${u.user_uuid.first_name || ""} ${u.user_uuid.last_name || ""
                }`.trim()
              : undefined,
            createdAt: u.createdAt,
          }))
          : wineStats?.recentUpdates || [];

      return {
        total: api.totalWines || wineStats?.total || items.length,
        newWines: api.newWinesInThirtyDays || wineStats?.newWines || 0,
        removed: api.removedWinesInThirtyDays || wineStats?.removed || 0,
        lastUpdated:
          api.recentWineUpdates && api.recentWineUpdates.length > 0
            ? api.recentWineUpdates[0].details?.wine_name ||
            api.recentWineUpdates[0].details?.product_name ||
            api.lastUpdatedWine
            : wineStats?.lastUpdated || "",
        wineCategory: (api.categoryDistribution &&
          Array.isArray(api.categoryDistribution)
          ? api.categoryDistribution.map((c) => ({
            label: c.name || c.label,
            value: Number(c.percentage || c.value || 0),
            count: c.count || 0,
          }))
          : categoryFromScalars
        ).sort((a, b) => {
          const order = [
            "Sparkling",
            "White",
            "Rose",
            "Rosé",
            "Red",
            "Orange",
            "Dessert",
          ];
          return order.indexOf(a.label) - order.indexOf(b.label);
        }),
        styleDistribution: normalizedStyleDistribution,
        topVarietals: normalizedTopVarietals,
        regionalDistribution: api.regions
          ? Object.entries(api.regions).map(([k, v]) => ({
            label: k,
            value: Number(v || 0),
          }))
          : regionalFromScalars.length
            ? regionalFromScalars
            : wineStats?.regionalDistribution || [],
        dietaryConsiderations: normalizedDietary,
        recentUpdates: normalizedRecent,
      };
    }
    return wineStats;
  }, [wineAnalyticsApi, wineStats, items]);

  const beerPanelStats = useMemo(() => {
    if (activeTab !== "beer") return null;

    const api = beerAnalyticsApi;
    const total = api?.totalBeers || items.length || 0;

    if (!api) {
      // Fallback: compute from items
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const newBeers = items.filter(i => new Date(i.createdAt) > thirtyDaysAgo).length;

      const lastUpdatedItem = items.reduce((latest, item) => {
        return new Date(item.updatedAt) > new Date(latest?.updatedAt || 0) ? item : latest;
      }, items[0]);

      const lastUpdated = lastUpdatedItem
        ? `${lastUpdatedItem.brewery_name || ''} ${lastUpdatedItem.product_name || ''}`.trim()
        : '';

      const catCounts = {};
      items.forEach(i => {
        const cat = i.category || i.style || 'Other';
        catCounts[cat] = (catCounts[cat] || 0) + 1;
      });
      const BeerCategory = Object.entries(catCounts)
        .map(([label, count]) => ({
          label,
          value: total > 0 ? Math.round((count / total) * 100) : 0,
          count,
        }))
        .sort((a, b) => b.value - a.value);

      const regCounts = {};
      items.forEach(i => {
        const cr = i.origin?.country;
        const r = (!cr || cr === '0') ? 'Unknown' : cr;
        regCounts[r] = (regCounts[r] || 0) + 1;
      });
      const regionalDistribution = Object.entries(regCounts)
        .map(([label, count]) => ({
          label,
          value: total > 0 ? Math.round((count / total) * 100) : 0,
        }))
        .sort((a, b) => b.value - a.value);

      const activeItems = items.filter(b => b.status === true);
      const activeItemsCount = activeItems.length;
      const pctOfActive = (n) => activeItemsCount > 0 ? `${Math.round((n / activeItemsCount) * 100)}%` : '0%';

      const dietaryConsiderations = [
        { label: 'Non-Alcoholic', count: activeItems.filter(b => b.is_non_alcoholic === true).length },
        { label: 'High ABV', count: activeItems.filter(b => typeof b.abv === 'number' && b.abv > 7.5).length },
        { label: 'Contains Lactose', count: activeItems.filter(b => b.has_lactose === true).length },
        { label: 'Contains Nuts', count: activeItems.filter(b => b.has_nuts === true).length },
        { label: 'Gluten-Free', count: activeItems.filter(b => b.has_gluten === false).length },
        { label: 'Vegan', count: activeItems.filter(b => b.is_vegan === true).length },
        { label: 'Filtered', count: activeItems.filter(b => b.is_filtered === true).length },
        { label: 'Unfiltered', count: activeItems.filter(b => b.is_filtered === false).length },
      ].map(d => ({ ...d, value: pctOfActive(d.count) }));

      return {
        total,
        newBeers,
        removed: 0,
        lastUpdated,
        BeerCategory,
        styleDistribution: [],
        topVarietals: [],
        regionalDistribution,
        dietaryConsiderations,
        recentUpdates: [],
      };
    }

    // Use API data
    return mapBeerAnalyticsToStats(api, total);
  }, [beerAnalyticsApi, activeTab, items]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(items.map((item) => item.uuid || item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Beer archive/restore mutates data that lives outside the items table
  // (analytics + activity feed), so it needs its own refetch on top of onRefresh.
  const refreshBeerDashboard = async () => {
    try {
      const target = restaurantUuid === null ? "all" : restaurantUuid;
      const [analyticsData, activityData] = await Promise.all([
        MenuService.getBeerAnalytics(target),
        MenuService.getBeerActivity(target, 1, 20),
      ]);
      setBeerAnalyticsApi(analyticsData);
      setBeerActivityData(activityData.activities);
      setBeerActivityPage(1);
      setBeerActivityHasMore(activityData.pagination.hasMore);
      setBeerActivityTotalCount(activityData.pagination.totalCount);
    } catch (err) {
      console.error("Failed to refresh beer dashboard:", err);
    }
  };

  const handleDelete = async (item) => {
    try {
      setDeleteLoading(true);
      if (activeTab === "food") {
        await MenuService.deleteDish(item.uuid);
      } else if (activeTab === "beer") {
        await MenuService.deleteBeer(item.uuid);
      } else {
        await MenuService.deleteWine(item.uuid);
      }
      // Close modal and clear loading state first
      setShowDeleteModal(false);
      setDeleteLoading(false);
      setDeleteItem(null);
      if (activeTab === "beer") refreshBeerDashboard();
      // Refresh table (use parent refresh if provided, fallback to full reload)
      setTimeout(() => {
        if (typeof onRefresh === "function") onRefresh();
        else window.location.reload();
      }, 100);
    } catch (err) {
      console.error("Failed to delete item:", err);
      // Always clear loading state and close modal on error
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setDeleteItem(null);
      // Optionally show error message to user
      alert("Failed to archive item. Please try again.");
    }
  };

  const handleRestore = async (item) => {
    try {
      setRestoreLoading(true);
      if (activeTab === "food") {
        await MenuService.restoreDish(item.uuid);
      } else if (activeTab === "beer") {
        await MenuService.restoreBeer(item.uuid);
      } else {
        await MenuService.restoreWine(item.uuid);
      }
      // Close modal and clear loading state first
      setShowRestoreModal(false);
      setRestoreLoading(false);
      setRestoreItem(null);
      if (activeTab === "beer") refreshBeerDashboard();
      // Refresh table (use parent refresh if provided, fallback to full reload)
      setTimeout(() => {
        if (typeof onRefresh === "function") onRefresh();
        else window.location.reload();
      }, 100);
    } catch (err) {
      console.error("Failed to restore item:", err);
      // Always clear loading state and close modal on error
      setRestoreLoading(false);
      setShowRestoreModal(false);
      setRestoreItem(null);
      // Optionally show error message to user
      alert("Failed to restore item. Please try again.");
    }
  };

  const handleBulkRestore = async () => {
    if (selectedItems.length === 0) return;

    try {
      setBulkRestoreLoading(true);
      const uuids = selectedItems.map((id) => id);

      if (activeTab === "food") {
        await MenuService.bulkUpdateDishes(uuids, "Restore Dishes");
      } else if (activeTab === "beer") {
        await MenuService.bulkUpdateBeers(uuids, "Restore Beers");
      } else {
        await MenuService.bulkUpdateWines(uuids, "Restore Wines");
      }

      // Close modal and clear state
      setShowBulkRestoreModal(false);
      setBulkRestoreLoading(false);
      setSelectedItems([]);
      if (activeTab === "beer") refreshBeerDashboard();

      // Refresh table
      setTimeout(() => {
        if (typeof onRefresh === "function") onRefresh();
        else window.location.reload();
      }, 100);
    } catch (err) {
      console.error("Failed to restore items:", err);
      setBulkRestoreLoading(false);
      alert(`Failed to restore some items. Please try again.`);
    }
  };

  const handleBulkRestoreAsDraft = async () => {
    if (selectedItems.length === 0) return;

    try {
      setBulkRestoreDraftLoading(true);
      const uuids = selectedItems.map((id) => id);

      if (activeTab === "food") {
        await MenuService.bulkUpdateDishes(uuids, "Restore Dishes as Draft");
      } else if (activeTab === "beer") {
        await MenuService.bulkUpdateBeers(uuids, "Restore Beers as Draft");
      } else {
        await MenuService.bulkUpdateWines(uuids, "Restore Wines as Draft");
      }

      // Close modal and clear state
      setShowBulkRestoreDraftModal(false);
      setBulkRestoreDraftLoading(false);
      setSelectedItems([]);
      if (activeTab === "beer") refreshBeerDashboard();

      // Refresh table
      setTimeout(() => {
        if (typeof onRefresh === "function") onRefresh();
        else window.location.reload();
      }, 100);
    } catch (err) {
      console.error("Failed to restore items as draft:", err);
      setBulkRestoreDraftLoading(false);
      alert(`Failed to restore some items as draft. Please try again.`);
    }
  };

  const handleFoodBulkConfirm = async (action, value) => {
    if (selectedItems.length === 0) return;
    try {
      setFoodBulkLoading(true);
      const uuids = selectedItems.map((id) => id);
      // Delegate to MenuService bulk endpoint. Backend should interpret action/value.
      await MenuService.bulkUpdateDishes(uuids, action, value);
      setShowFoodBulkModal(false);
      setSelectedItems([]);
      setFoodBulkLoading(false);
      // Refresh to reflect changes (call parent refresh if available)
      setTimeout(() => {
        if (typeof onRefresh === "function") onRefresh();
        else window.location.reload();
      }, 100);
    } catch (err) {
      console.error("Food bulk action failed:", err);
      setFoodBulkLoading(false);
      alert("Failed to apply bulk action. Please try again.");
    }
  };

  const handleWineBulkConfirm = async (action, value) => {
    if (selectedItems.length === 0) return;
    try {
      setWineBulkLoading(true);
      const uuids = selectedItems.map((id) => id);
      await MenuService.bulkUpdateWines(uuids, action, value);
      setShowWineBulkModal(false);
      setSelectedItems([]);
      setWineBulkLoading(false);
      setTimeout(() => {
        if (typeof onRefresh === "function") onRefresh();
        else window.location.reload();
      }, 100);
    } catch (err) {
      console.error("Wine bulk action failed:", err);
      setWineBulkLoading(false);
      alert("Failed to apply bulk wine action. Please try again.");
    }
  };

  const handleBeerBulkConfirm = async (action, value) => {
    if (selectedItems.length === 0) return;
    try {
      setBeerBulkLoading(true);
      const uuids = selectedItems.map((id) => id);
      await MenuService.bulkUpdateBeers(uuids, action, value);
      setShowBeerBulkModal(false);
      setSelectedItems([]);
      setBeerBulkLoading(false);
      refreshBeerDashboard();
      setTimeout(() => {
        if (typeof onRefresh === "function") onRefresh();
        else window.location.reload();
      }, 100);
    } catch (err) {
      console.error("Beer bulk action failed:", err);
      setBeerBulkLoading(false);
      alert("Failed to apply bulk beer action. Please try again.");
    }
  };

  const userRole = localStorage.getItem("userRole");

  // Get style badge color matching category with lighter opacity
  const getStyleBadgeColor = (category) => {
    const categoryLower = category?.toLowerCase() || "";
    if (categoryLower === "red") {
      return "bg-red-50 text-red-700 border-red-100";
    } else if (categoryLower === "white") {
      return "bg-amber-50 text-amber-700 border-amber-100";
    } else if (categoryLower === "sparkling") {
      return "bg-yellow-50 text-yellow-700 border-yellow-100";
    } else if (categoryLower === "rosé" || categoryLower === "rose") {
      return "bg-pink-50 text-pink-700 border-pink-100";
    } else if (categoryLower === "orange") {
      return "bg-orange-50 text-orange-700 border-orange-100";
    } else if (categoryLower === "dessert") {
      return "bg-amber-50 text-amber-700 border-amber-100";
    }
    return "bg-gray-50 text-gray-700 border-gray-100";
  };

  // Renders one row per saved serving-size/price pair for a beer format,
  // instead of collapsing multiple sizes (e.g. two Draft entries) into one.
  const renderBeerFormatRows = (format, label, badgeClass) => {
    if (!format) return null;
    const sizes = format.serving_sizes?.length ? format.serving_sizes : ['oz'];
    return sizes.map((size, idx) => (
      <div className="flex items-center gap-3" key={`${label}-${idx}`}>
        <span className={`text-[11px] font-bold ${badgeClass} rounded px-2 py-1 uppercase min-w-[54px] text-center`}>{label}</span>
        <span className="text-lg font-black text-gray-900 ml-1">${format.prices?.[idx] ?? '0'}</span>
        <span className="text-[12px] text-gray-500 font-medium">/ {size || 'oz'}</span>
      </div>
    ));
  };

  const renderSortIcon = (field) => {
    if (!onSortChange) return null;
    if (sortBy !== field) {
      return <span className="ml-1 text-[11px] text-gray-400">⇅</span>;
    }
    return (
      <span className="ml-1 text-[11px] text-gray-400">
        {sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  // analytics state moved above to avoid TDZ ReferenceError

  // Tag color mapping
  const getTagStyle = (type, value) => {
    const valueLower = String(value).toLowerCase();

    if (type === "allergen") {
      if (valueLower.includes("fish"))
        return "bg-red-50 text-red-600 border-red-100";
      if (valueLower.includes("gluten"))
        return "bg-red-50 text-red-600 border-red-100";
      if (valueLower.includes("egg"))
        return "bg-red-50 text-red-600 border-red-100";
      if (valueLower.includes("dairy"))
        return "bg-red-50 text-red-600 border-red-100";
      return "bg-red-50 text-red-600 border-red-100";
    }

    if (type === "health") {
      if (valueLower.includes("fish"))
        return "bg-blue-50 text-blue-600 border-blue-100";
      if (valueLower.includes("dairy"))
        return "bg-blue-50 text-blue-600 border-blue-100";
      if (valueLower.includes("gluten"))
        return "bg-blue-50 text-blue-600 border-blue-100";
      return "bg-blue-50 text-blue-600 border-blue-100";
    }

    if (type === "belief") {
      return "bg-purple-50 text-purple-600 border-purple-100";
    }

    if (type === "lifestyle") {
      if (valueLower.includes("vegetarian"))
        return "bg-green-50 text-green-600 border-green-100";
      if (valueLower.includes("vegan"))
        return "bg-green-50 text-green-600 border-green-100";
      if (valueLower.includes("pescatarian"))
        return "bg-green-50 text-green-600 border-green-100";
      return "bg-green-50 text-green-600 border-green-100";
    }

    if (type === "accommodation") {
      return "bg-yellow-50 text-yellow-700 border-yellow-100";
    }

    return "bg-gray-50 text-gray-600 border-gray-100";
  };

  // Fetch analytics when restaurantUuid or activeTab changes
  useEffect(() => {
    const target = restaurantUuid === null ? "all" : restaurantUuid;
    const status = userRole === "employee" ? "active" : undefined;

    const fetchAnalytics = async () => {
      try {
        if (activeTab === "food") {
          const data = await MenuService.getDishAnalytics(target, status);
          setDishAnalytics(data);
        } else if (activeTab === "wine") {
          const data = await MenuService.getWineAnalytics(target);
          setWineAnalyticsApi(data);
        } else if (activeTab === "beer") {
          const data = await MenuService.getBeerAnalytics(target);
          setBeerAnalyticsApi(data);
        }
      } catch (err) {
        console.error("Failed to fetch analytics:", err);
      }
    };

    fetchAnalytics();
  }, [restaurantUuid, activeTab]);

  const localDishStats = useMemo(() => {
    const total = totalItems || items.length;
    const activeCount = items.filter((i) => i.status).length;
    const activeRate = total ? ((activeCount / total) * 100).toFixed(1) : "0.0";
    const recentCount = items.filter((i) => {
      const diff = Date.now() - new Date(i.updatedAt).getTime();
      return diff <= 7 * 24 * 60 * 60 * 1000;
    }).length;
    return { total, activeCount, activeRate, recentCount };
  }, [items]);

  // Fetch beer activity data with pagination
  const fetchBeerActivity = useCallback(async (page = 1, append = false) => {
    if (beerActivityLoading) return;

    setBeerActivityLoading(true);
    try {
      const target = restaurantUuid === null ? "all" : restaurantUuid;
      const data = await MenuService.getBeerActivity(target, page, 20);

      if (append) {
        setBeerActivityData(prev => [...prev, ...data.activities]);
      } else {
        setBeerActivityData(data.activities);
      }

      setBeerActivityPage(page);
      setBeerActivityHasMore(data.pagination.hasMore);
      setBeerActivityTotalCount(data.pagination.totalCount);
    } catch (err) {
      console.error("Failed to fetch beer activity:", err);
    } finally {
      setBeerActivityLoading(false);
    }
  }, [restaurantUuid, beerActivityLoading]);

  // Initial beer activity fetch when restaurant or tab changes
  useEffect(() => {
    if (activeTab === "beer" && !previousTable) {
      setBeerActivityData([]);
      setBeerActivityPage(1);
      setBeerActivityHasMore(true);
      fetchBeerActivity(1, false);
    }
  }, [restaurantUuid, activeTab, previousTable]);

  // Load more beer activity when the user scrolls within 100px of the bottom
  const handleBeerActivityScroll = useCallback((e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    if (scrollHeight - scrollTop - clientHeight < 100 && beerActivityHasMore && !beerActivityLoading) {
      fetchBeerActivity(beerActivityPage + 1, true);
    }
  }, [beerActivityHasMore, beerActivityLoading, beerActivityPage, fetchBeerActivity]);

  return (
    <div className="bg-[#FFF9F3] rounded-xl border border-[#FDE6D2] overflow-hidden">
      {/* Header - Row 1: Title and Action Buttons */}
      <div className="px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-[#FDE6D2]">
        <h3 className="text-sm font-bold text-gray-900">
          Menu Items{" "}
          {totalItems > 0 && (
            <span className="ml-1 text-gray-400 font-medium">
              ({totalItems})
            </span>
          )}
        </h3>

        <div className="flex flex-wrap items-center gap-2">
          {/* View Toggle */}
          <div className="flex bg-white rounded-md overflow-hidden border border-gray-200">
            <button
              onClick={() => setViewMode("table")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-all ${viewMode === "table"
                ? "bg-[#881337] text-white"
                : "text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 10h18M3 6h18M3 14h18M3 18h18"
                />
              </svg>
              Table View
            </button>
            <button
              onClick={() => setViewMode("card")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-all ${viewMode === "card"
                ? "bg-[#881337] text-white"
                : "text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
              Card View
            </button>
          </div>

          {/* Food Tab Buttons */}
          {activeTab === "food" && userRole !== "employee" && !previousTable && (
            <>
              <button
                onClick={() => setViewMode("allergen-reference")}
                className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs font-semibold transition-all ${viewMode === "allergen-reference"
                  ? "bg-[#881337] text-white border-[#881337]"
                  : "bg-white border-[#881337] text-[#881337] hover:bg-[#FFF5F5]"
                  }`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Allergens, Restrictions, and Accommodations Menu
              </button>
              <button
                onClick={() => setViewMode("allergen-matrix")}
                className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs font-semibold transition-all ${viewMode === "allergen-matrix"
                  ? "bg-[#881337] text-white border-[#881337]"
                  : "bg-white border-[#881337] text-[#881337] hover:bg-[#FFF5F5]"
                  }`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Allergen and Accommodation Matrix
              </button>
            </>
          )}

          {/* Wine Reference Sheet Button - Only for Wine Tab */}
          {activeTab === "wine" &&
            userRole !== "employee" &&
            !previousTable && (
              <button
                onClick={() => setShowWineReferenceSheet(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#881337] text-[#881337] rounded-md text-xs font-semibold hover:bg-[#FFF5F5] transition-all"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                Wine Reference Sheet
              </button>
            )}

          {/* Beer Reference Sheet Button - Only for Beer Tab */}
          {activeTab === "beer" &&
            userRole !== "employee" &&
            !previousTable && (
              <button
                onClick={() => setShowBeerReferenceSheet(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#881337] text-[#881337] rounded-md text-xs font-semibold hover:bg-[#FFF5F5] transition-all"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                Beer Reference Sheet
              </button>
            )}
        </div>
      </div>
      {/* Header - Row 2: Bulk Actions, Show, and Count */}
      <div className="px-4 py-2.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-[#FFF9F3]">
        <div className="flex items-center gap-3">
          {/* Bulk Actions */}
          {userRole !== "employee" && (
            <Dropdown
              label="Bulk Actions"
              options={(() => {
                const opts = [];
                if (previousTable) {
                  opts.push({
                    value: "restore",
                    label: "Restore to Current Menu",
                  });
                }
                if (activeTab === "food" && !previousTable) {
                  opts.push({
                    value: "food-bulk",
                    label: "Manage Selected Dishes...",
                  });
                }
                if (activeTab === "wine" && !previousTable) {
                  opts.push({
                    value: "wine-bulk",
                    label: "Manage Selected Wines...",
                  });
                }
                if (activeTab === "beer" && !previousTable) {
                  opts.push({
                    value: "beer-bulk",
                    label: "Manage Selected Beers...",
                  });
                }
                return opts.length
                  ? opts
                  : [{ value: "none", label: "Bulk Actions" }];
              })()}
              selectedOption={""}
              onSelect={(option) => {
                const val = option?.value;
                if (
                  val === "restore" &&
                  previousTable &&
                  activeTab === "beer" &&
                  selectedItems.length > 0
                ) {
                  // Beer's Previous Menu restore goes through the same
                  // Bulk Actions modal (Status → Restore to Current Menu)
                  // instead of the plain generic restore confirm dialog.
                  setShowBeerBulkModal(true);
                } else if (
                  val === "restore" &&
                  previousTable &&
                  selectedItems.length > 0
                ) {
                  setShowBulkRestoreModal(true);
                } else if (
                  val === "food-bulk" &&
                  activeTab === "food" &&
                  !previousTable &&
                  selectedItems.length > 0
                ) {
                  setShowFoodBulkModal(true);
                } else if (
                  val === "wine-bulk" &&
                  activeTab === "wine" &&
                  !previousTable &&
                  selectedItems.length > 0
                ) {
                  setShowWineBulkModal(true);
                } else if (
                  val === "beer-bulk" &&
                  activeTab === "beer" &&
                  !previousTable &&
                  selectedItems.length > 0
                ) {
                  setShowBeerBulkModal(true);
                }
              }}
              className="min-w-[160px]"
              buttonClassName="h-9 px-3 py-1 text-sm text-gray-700 font-semibold border-gray-200"
              disabled={selectedItems.length === 0}
            />
          )}

        </div>
      </div>

      {/* View Content */}
      <div className="overflow-x-auto">
        {viewMode === "allergen-reference" ? (
          <AllergenReferenceMenu
            isOpen={false}
            onClose={() => setViewMode("table")}
            items={items}
            selectedItems={selectedItems}
            onFetchAllItems={onFetchAllItems}
          />
        ) : viewMode === "allergen-matrix" ? (
          <AllergenAccommodationMatrix
            isOpen={false}
            onClose={() => setViewMode("table")}
            items={items}
            selectedItems={selectedItems}
            onFetchAllItems={onFetchAllItems}
          />
        ) : viewMode === "table" ? (
          <table className="w-full border-collapse">
            <thead>
              {activeTab === "food" ? (
                <tr className="bg-white border-y border-[#FDE6D2]">
                  {userRole !== "employee" && (
                    <th className="px-1 py-2 text-left w-8">
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          selectedItems.length === items.length &&
                          items.length > 0
                        }
                        className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3 h-3"
                      />
                    </th>
                  )}
                  <th
                    className={`py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter cursor-pointer select-none hover:bg-gray-50 ${userRole === "employee" ? "pl-8 pr-4" : "px-2"
                      }`}
                    onClick={() => onSortChange && onSortChange("name")}
                  >
                    <div className="flex items-center gap-0.5">
                      Dish Name
                      {renderSortIcon("name")}
                    </div>
                  </th>
                  <th
                    className="px-4 py-1 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("category")}
                  >
                    <div className="flex items-center gap-0.5">
                      Category
                      {renderSortIcon("category")}
                    </div>
                  </th>
                  <th
                    className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("restaurant")}
                  >
                    <div className="flex items-center gap-0.5">
                      Restaurant
                      {renderSortIcon("restaurant")}
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter">
                    Allergens
                  </th>
                  <th className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter">
                    Health
                  </th>
                  <th className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter">
                    Belief Accommodations
                  </th>
                  <th className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter">
                    Lifestyle Accommodations
                  </th>
                  <th className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter">
                    Accommodations
                  </th>
                  {previousTable && (
                    <th
                      className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter cursor-pointer select-none hover:bg-gray-50"
                      onClick={() =>
                        onSortChange && onSortChange("onMenuDisplay")
                      }
                    >
                      <div className="flex items-center gap-0.5">
                        On Menu
                        {renderSortIcon("onMenuDisplay")}
                      </div>
                    </th>
                  )}
                  <th
                    className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("price")}
                  >
                    <div className="flex items-center gap-0.5">
                      Price
                      {renderSortIcon("price")}
                    </div>
                  </th>
                  <th
                    className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("training_completion")}
                  >
                    <div className="flex items-center gap-0.5">
                      Training Completion
                      {renderSortIcon("training_completion")}
                    </div>
                  </th>
                  <th
                    className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("updatedAt")}
                  >
                    <div className="flex items-center gap-0.5">
                      Updated
                      {renderSortIcon("updatedAt")}
                    </div>
                  </th>
                  {userRole !== "employee" && (
                    <th
                      className={`py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter cursor-pointer select-none hover:bg-gray-50 ${userRole === "employee" ? "pl-4 pr-8" : "px-4"
                        }`}
                      onClick={() => onSortChange && onSortChange("status")}
                    >
                      <div className="flex items-center gap-0.5">
                        Status
                        {renderSortIcon("status")}
                      </div>
                    </th>
                  )}
                  {userRole !== "employee" && (
                    <th className="px-4 py-2 text-left text-[12px] font-bold text-gray-600 uppercase tracking-tighter">
                      Actions
                    </th>
                  )}
                </tr>
              ) : activeTab === "beer" ? (
                <tr className="bg-white border-y border-[#FDE6D2]">
                  {userRole !== "employee" && (
                    <th className="pl-4 pr-2 py-3 text-left min-w-[40px]">
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedItems.length === items.length && items.length > 0}
                        className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5"
                      />
                    </th>
                  )}
                  <th className={"py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[240px] cursor-pointer select-none hover:bg-gray-50 " + (userRole === "employee" ? "pl-8 pr-4" : "pl-4 pr-2")} onClick={() => onSortChange && onSortChange("name")}>
                    <div className="flex items-center gap-0.5">BEER {renderSortIcon("name")}</div>
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[120px] cursor-pointer select-none hover:bg-gray-50" onClick={() => onSortChange && onSortChange("category")}>
                    <div className="flex items-center gap-0.5">STYLE GROUP {renderSortIcon("category")}</div>
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[130px] cursor-pointer select-none hover:bg-gray-50" onClick={() => onSortChange && onSortChange("style")}>
                    <div className="flex items-center gap-0.5">FAMILY / CORE STYLE {renderSortIcon("style")}</div>
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[140px]">
                    ORIGIN
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[140px]">
                    STRENGTH & BITTERNESS
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[180px]">
                    BEER PROFILE
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[120px]">
                    ATTRIBUTES
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[140px]">
                    FORMAT & PRICE
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[150px] cursor-pointer select-none hover:bg-gray-50" onClick={() => onSortChange && onSortChange("training_completion")}>
                    <div className="flex items-center gap-0.5">TRAINING {renderSortIcon("training_completion")}</div>
                  </th>
                  {previousTable && (
                    <th className="px-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight cursor-pointer select-none hover:bg-gray-50" onClick={() => onSortChange && onSortChange("onMenuDisplay")}>
                      <div className="flex items-center gap-0.5">ON MENU {renderSortIcon("onMenuDisplay")}</div>
                    </th>
                  )}
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[90px] cursor-pointer select-none hover:bg-gray-50" onClick={() => onSortChange && onSortChange("updatedAt")}>
                    <div className="flex items-center gap-0.5">UPDATED {renderSortIcon("updatedAt")}</div>
                  </th>
                  {userRole !== "employee" && (
                    <th className={"py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[80px] cursor-pointer select-none hover:bg-gray-50 " + (userRole === "employee" ? "pl-4 pr-8" : "pl-4 pr-2")} onClick={() => onSortChange && onSortChange("status")}>
                      <div className="flex items-center gap-0.5">STATUS {renderSortIcon("status")}</div>
                    </th>
                  )}
                  {userRole !== "employee" && (
                    <th className="pl-4 pr-2 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[70px]">ACTIONS</th>
                  )}
                </tr>
              ) : (
                <tr className="bg-white border-y border-[#FDE6D2]">
                  {userRole !== "employee" && (
                    <th className="pl-4 pr-2 py-3 text-left min-w-[40px]">
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          selectedItems.length === items.length &&
                          items.length > 0
                        }
                        className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5"
                      />
                    </th>
                  )}
                  <th
                    className={`py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[240px] cursor-pointer select-none hover:bg-gray-50 ${userRole === "employee" ? "pl-8 pr-4" : "pl-4 pr-2"
                      }`}
                    onClick={() => onSortChange && onSortChange("name")}
                  >
                    <div className="flex items-center gap-0.5">
                      Wine
                      {renderSortIcon("name")}
                    </div>
                  </th>
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[60px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("vintage")}
                  >
                    <div className="flex items-center gap-0.5">
                      Vintage
                      {renderSortIcon("vintage")}
                    </div>
                  </th>
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[100px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("category")}
                  >
                    <div className="flex items-center gap-0.5">
                      Category
                      {renderSortIcon("category")}
                    </div>
                  </th>
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[120px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("restaurant")}
                  >
                    <div className="flex items-center gap-0.5">
                      Restaurant
                      {renderSortIcon("restaurant")}
                    </div>
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[160px]">
                    Varietals
                  </th>
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[160px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("region")}
                  >
                    <div className="flex items-center gap-0.5">
                      Region
                      {renderSortIcon("region")}
                    </div>
                  </th>
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[130px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("style")}
                  >
                    <div className="flex items-center gap-0.5">
                      Style
                      {renderSortIcon("style")}
                    </div>
                  </th>
                  <th className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[140px]">
                    Characteristics
                  </th>
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[100px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("offering")}
                  >
                    <div className="flex items-center gap-0.5">
                      Offering
                      {renderSortIcon("offering")}
                    </div>
                  </th>
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[120px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("price")}
                  >
                    <div className="flex items-center gap-0.5">
                      Price
                      {renderSortIcon("price")}
                    </div>
                  </th>
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[150px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("training_completion")}
                  >
                    <div className="flex items-center gap-0.5">
                      Training Completion
                      {renderSortIcon("training_completion")}
                    </div>
                  </th>
                  {previousTable && (
                    <th
                      className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[100px] cursor-pointer select-none hover:bg-gray-50"
                      onClick={() =>
                        onSortChange && onSortChange("onMenuDisplay")
                      }
                    >
                      <div className="flex items-center gap-0.5">
                        On Menu
                        {renderSortIcon("onMenuDisplay")}
                      </div>
                    </th>
                  )}
                  <th
                    className="pl-4 pr-4 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[90px] cursor-pointer select-none hover:bg-gray-50"
                    onClick={() => onSortChange && onSortChange("updatedAt")}
                  >
                    <div className="flex items-center gap-0.5">
                      Updated
                      {renderSortIcon("updatedAt")}
                    </div>
                  </th>
                  {userRole !== "employee" && (
                    <th
                      className={`py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[80px] cursor-pointer select-none hover:bg-gray-50 ${userRole === "employee" ? "pl-4 pr-8" : "pl-4 pr-2"
                        }`}
                      onClick={() => onSortChange && onSortChange("status")}
                    >
                      <div className="flex items-center gap-0.5">
                        Status
                        {renderSortIcon("status")}
                      </div>
                    </th>
                  )}
                  {userRole !== "employee" && (
                    <th className="pl-4 pr-2 py-3 text-left text-[12px] font-bold text-gray-500 uppercase tracking-tight min-w-[70px]">
                      Actions
                    </th>
                  )}
                </tr>
              )}
            </thead>
            <tbody className="bg-white divide-y divide-[#FDE6D2]">
              {loading ? (
                <tr>
                  <td colSpan="14" className="px-4 py-10 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-3 border-[#881337]/20 border-t-[#881337]"></div>
                      <p className="mt-2 text-[12px] font-medium text-gray-500">
                        Loading...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      activeTab === "food"
                        ? userRole !== "employee"
                          ? "13"
                          : "11"
                        : userRole !== "employee"
                          ? "14"
                          : "12"
                    }
                    className="px-4 py-8 text-center text-gray-500 text-[12px] font-medium"
                  >
                    No {activeTab === 'food' ? 'dishes' : activeTab === 'beer' ? 'beers' : 'wines'} found
                  </td>
                </tr>
              ) : activeTab === "food" ? (
                items.map((item) => (
                  <tr
                    key={item.uuid}
                    className="hover:bg-[#FFF9F3] transition-colors group"
                  >
                    {userRole !== "employee" && (
                      <td className="px-1 py-2">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.uuid || item.id)}
                          onChange={() =>
                            handleSelectItem(item.uuid || item.id)
                          }
                          className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3 h-3"
                        />
                      </td>
                    )}
                    <td
                      className={`py-2 ${userRole === "employee" ? "pl-8 pr-4" : "px-2"
                        }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <div className="h-10 w-10 rounded border border-gray-100 shadow-sm flex-shrink-0">
                          {item.image_url ? (
                            <img
                              src={
                                item.image_url.startsWith("https")
                                  ? item.image_url
                                  : `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url}`
                              }
                              alt={item.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="h-full w-full bg-gray-50 flex items-center justify-center text-gray-300">
                              <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className="text-[12px] font-bold text-[#111827] leading-tight max-w-[120px] break-words">
                          {item.name || item.product_name || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="px-1 py-0.5 rounded-full text-[11px] font-bold bg-orange-50 text-orange-600 border border-orange-100 uppercase whitespace-nowrap">
                        {item.typeDisplay || item.category || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-[12px] font-semibold text-gray-600 block max-w-[100px] truncate">
                        {item.restaurantNameDisplay ||
                          item.restaurantname ||
                          "-"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-0.5 max-w-[100px]">
                        {item.allergens?.length > 0 ? (
                          item.allergens.map((alg, i) => (
                            <span
                              key={i}
                              className={`px-1 py-0 rounded border text-[11px] font-bold ${getTagStyle(
                                "allergen",
                                alg
                              )}`}
                            >
                              {alg}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            None
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-0.5 max-w-[100px]">
                        {item.dietary_restrictions?.health?.length > 0 ? (
                          item.dietary_restrictions.health.map((h, i) => (
                            <span
                              key={i}
                              className={`px-1 py-0 rounded border text-[11px] font-bold ${getTagStyle(
                                "health",
                                h
                              )}`}
                            >
                              {h}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            None
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-0.5 max-w-[100px]">
                        {item.dietary_restrictions?.belief?.length > 0 ? (
                          item.dietary_restrictions.belief.map((b, i) => (
                            <span
                              key={i}
                              className={`px-1 py-0 rounded border text-[11px] font-bold ${getTagStyle(
                                "belief",
                                b
                              )}`}
                            >
                              {b}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            None
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-0.5 max-w-[100px]">
                        {item.dietary_restrictions?.lifestyle?.length > 0 ? (
                          item.dietary_restrictions.lifestyle.map((l, i) => (
                            <span
                              key={i}
                              className={`px-1 py-0 rounded border text-[11px] font-bold ${getTagStyle(
                                "lifestyle",
                                l
                              )}`}
                            >
                              {l}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            None
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-0.5 max-w-[100px]">
                        {item.accommodations?.length > 0 ? (
                          item.accommodations.map((acc, i) => (
                            <span
                              key={i}
                              className={`px-1 py-0 rounded border text-[11px] font-bold ${getTagStyle(
                                "accommodation",
                                acc
                              )}`}
                            >
                              {acc}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            None
                          </span>
                        )}
                      </div>
                    </td>

                    {previousTable && (
                      <td className="px-4 py-2">
                        <span className="text-[12px] font-bold text-gray-900 whitespace-nowrap">
                          {item.onMenuDisplay || "—"}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-2">
                      <span className="text-[12px] font-black text-gray-900">
                        ${item.price?.toFixed(2) || "0.00"}
                      </span>
                    </td>

                    <td className="px-4 py-2">
                      {item.training_stats?.denominator > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 transition-all duration-500"
                              style={{ width: `${item.training_stats.completion_percentage}%` }}
                            />
                          </div>
                          <span className="text-[12px] font-black text-gray-700">
                            {item.training_stats.completion_percentage}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-[12px] font-bold text-gray-300">—</span>
                      )}
                    </td>

                    <td className="px-4 py-2">
                      <span className="text-[11px] font-bold text-gray-500 whitespace-nowrap">
                        {formatDate(item.updatedAt)}
                      </span>
                    </td>
                    {userRole !== "employee" && (
                      <td
                        className={`py-2 ${userRole === "employee" ? "pl-4 pr-8" : "px-4"
                          }`}
                      >
                        <span
                          className={`px-1 py-0 rounded-full text-[11px] font-black uppercase ${item.status
                            ? "bg-green-50 text-green-600 border border-green-100"
                            : "bg-gray-100 text-gray-700"
                            }`}
                        >
                          {item.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                    )}
                    {userRole !== "employee" && (
                      <td className="px-1 py-2">
                        <div className="flex items-center gap-1">
                          {previousTable ? (
                            <button
                              onClick={() => {
                                setRestoreItem(item);
                                setShowRestoreModal(true);
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#881337] text-white rounded-md text-[12px] font-bold hover:bg-[#881337]/90 transition-all"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2.5"
                                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                                />
                              </svg>
                              Undo
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => onEditItem(item)}
                                className="p-1 text-gray-400 hover:text-[#881337] hover:bg-[#881337]/5 rounded transition-all"
                              >
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteItem(item);
                                  setShowDeleteModal(true);
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                              >
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : activeTab === "beer" ? (
                items.map((item) => (
                  <tr
                    key={item.uuid}
                    className="hover:bg-[#FFF9F3] transition-colors group border-b border-gray-50/50"
                  >
                    {userRole !== "employee" && (
                      <td className="pl-4 pr-2 py-3 align-top text-left min-w-[40px]">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.uuid || item.id)}
                          onChange={() => handleSelectItem(item.uuid || item.id)}
                          className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5 mt-1"
                        />
                      </td>
                    )}
                    <td className="pl-4 pr-2 py-3 align-top text-left min-w-[240px]">
                      <div className="flex items-start gap-3">
                        <div className="h-14 w-12 bg-gray-100 rounded shadow-sm border border-gray-100 flex-shrink-0 overflow-hidden">
                          <BeerImage
                            src={item.image_url}
                            alt={item.product_name}
                            className="h-full w-full object-cover"
                            fallbackClassName="h-full w-full object-contain"
                          />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-[13px] font-bold text-gray-900 leading-tight truncate w-full block">
                            {item.product_name || item.name || "-"}
                          </span>
                          <span className="text-[12px] text-gray-500 leading-tight mt-0.5 truncate w-full block">
                            {item.brewery_name || "-"}
                          </span>
                        </div>
                      </div>
                    </td>
                    {/* STYLE GROUP */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[120px]">
                      {item.category ? (
                        <span className={`px-2.5 py-0.5 rounded-full text-[12px] font-bold border inline-block whitespace-nowrap ${getBeerCategoryColor(item.category)}`}>
                          {item.category}
                        </span>
                      ) : (
                        <span className="text-[12px] text-gray-400">-</span>
                      )}
                    </td>

                    {/* FAMILY / CORE STYLE */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[130px]">
                      <div className="flex flex-col items-start gap-1">
                        {getFermentationFamily(item.style) && (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase whitespace-nowrap ${getFermentationFamily(item.style) === "Ale"
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-blue-50 text-blue-700 border-blue-200"
                            }`}>
                            {getFermentationFamily(item.style)}
                          </span>
                        )}
                        {item.style ? (
                          <span className={`px-2.5 py-0.5 rounded-full text-[12px] font-bold border inline-block whitespace-nowrap ${getBeerCategoryColor(item.category)}`}>
                            {item.style}
                          </span>
                        ) : (
                          <span className="text-[12px] text-gray-400">-</span>
                        )}
                      </div>
                    </td>

                    {/* ORIGIN */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[160px]">
                      <div className="flex flex-col items-start gap-1 text-[12px] text-gray-700 leading-tight min-w-0">
                        {getOriginClassification(item.origin?.country) && (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase whitespace-nowrap ${getOriginClassification(item.origin?.country) === "DOMESTIC"
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                            }`}>
                            {getOriginClassification(item.origin?.country)}
                          </span>
                        )}
                        <span className="truncate font-medium">
                          {item.origin?.country || "-"}
                        </span>
                        {item.origin?.state && (
                          <span className="text-gray-500 truncate pl-2 flex items-center gap-1">
                            <span className="text-gray-400">↳</span>{item.origin.state}
                          </span>
                        )}
                        {item.origin?.city && (
                          <span className="text-gray-400 text-[12px] truncate pl-4 flex items-center gap-1">
                            <span className="text-gray-400">↳</span>{item.origin.city}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* STRENGTH & BITTERNESS */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[140px]">
                      <div className="flex flex-col text-[11px] font-bold text-gray-600 gap-1.5 mt-0.5">
                        <div className="flex items-center gap-2">
                          <span className="w-16">ABV: {item.abv ?? "0"}%</span>
                          {getAbvCategory(item.abv) && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] tracking-wide border capitalize ${getBeerLevelColor(getAbvCategory(item.abv))}`}>
                              {getAbvCategory(item.abv)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-16">IBU: {item.ibu ?? "0"}</span>
                          {getIbuCategory(item.ibu) && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] tracking-wide border capitalize ${getBeerLevelColor(getIbuCategory(item.ibu))}`}>
                              {getIbuCategory(item.ibu)}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* PROFILE */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[180px]">
                      <div className="flex flex-col text-[11px] font-medium text-gray-700 gap-1">
                        {item.body ? <span><span className="font-bold text-gray-500">Body:</span> <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-200 rounded tracking-tight">{item.body}</span></span> : <span><span className="font-bold text-gray-400">Body:</span> -</span>}
                        {item.texture ? <span><span className="font-bold text-gray-500">Texture:</span> <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded tracking-tight">{item.texture}</span></span> : <span><span className="font-bold text-gray-400">Texture:</span> -</span>}
                        {item.intensity ? <span><span className="font-bold text-gray-500">Flavor Intensity:</span> <span className="text-purple-600 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded font-bold tracking-tight">{item.intensity}</span></span> : <span><span className="font-bold text-gray-400">Flavor Intensity:</span> -</span>}
                      </div>
                    </td>

                    {/* ATTRIBUTES */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[120px]">
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {item.is_non_alcoholic && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-bold tracking-tight">Non-Alc</span>}
                        {item.is_vegan && <span className="px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-100 rounded text-[10px] font-bold tracking-tight">Vegan</span>}
                        {item.has_lactose && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-100 rounded text-[10px] font-bold tracking-tight">Lactose</span>}
                        {item.has_nuts && <span className="px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-100 rounded text-[10px] font-bold tracking-tight">Nuts</span>}
                        {item.has_gluten === false && <span className="px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-100 rounded text-[10px] font-bold tracking-tight">Gluten-Free</span>}
                        {item.is_filtered && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-200 rounded text-[10px] font-bold tracking-tight">Filtered</span>}
                        {item.seasonal && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-100 rounded text-[10px] font-bold tracking-tight">Seasonal</span>}
                        {item.limited && <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded text-[10px] font-bold tracking-tight">Limited Release</span>}
                        {!(item.is_non_alcoholic || item.is_vegan || item.has_lactose || item.has_nuts || item.has_gluten === false || item.is_filtered || item.seasonal || item.limited) && (
                          <span className="text-[11px] text-gray-400">-</span>
                        )}
                      </div>
                    </td>

                    {/* FORMAT & PRICE */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[140px]">
                      <div className="flex flex-col gap-1.5">
                        {[
                          { format: "draft", label: "DRAFT", badgeClass: "bg-blue-50 text-blue-600 border-blue-100" },
                          { format: "bottle", label: "BOTTLE", badgeClass: "bg-purple-50 text-purple-600 border-purple-100" },
                          { format: "can", label: "CAN", badgeClass: "bg-teal-50 text-teal-600 border-teal-100" },
                        ].map(({ format, label, badgeClass }) => {
                          const entry = item.formats?.find(f => f.format === format);
                          if (!entry) return null;
                          return (
                            <div key={format} className="text-[11px] text-gray-700 leading-tight flex items-center flex-wrap gap-1">
                              <span className={`font-bold text-[9px] px-1 py-0.5 border rounded uppercase ${badgeClass}`}>{label}</span>
                              <span>{entry.serving_sizes?.join(', ') || 'N/A'}</span>
                              <span className="text-green-600 font-medium">({entry.prices?.map(p => '$' + p).join(', ') || '-'})</span>
                            </div>
                          );
                        })}
                        {!item.formats?.length && (
                          <span className="text-[11px] text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="pl-4 pr-4 py-3 align-top min-w-[150px]">
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 transition-all" style={{ width: `${item.training_stats?.denominator > 0 ? item.training_stats.completion_percentage : 0}%` }} />
                        </div>
                        <span className="text-[12px] font-black text-gray-700">{item.training_stats?.denominator > 0 ? item.training_stats.completion_percentage : 0}%</span>
                      </div>
                    </td>
                    {previousTable && (
                      <td className="px-4 py-3"><span className="text-[12px] font-bold text-gray-900">{item.onMenuDisplay || "—"}</span></td>
                    )}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[90px]">
                      <span className="text-[11px] font-bold text-gray-500">{formatDate(item.updatedAt)}</span>
                    </td>
                    {userRole !== "employee" && (
                      <td className="pl-4 pr-2 py-3 align-top text-left">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black uppercase ${item.status ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-gray-100 text-gray-700'}`}>
                          {item.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                    )}
                    {userRole !== "employee" && (
                      <td className="pl-4 pr-2 py-3 align-top text-left">
                        <div className="flex flex-col gap-1.5">
                          {previousTable ? (
                            <button onClick={() => { setRestoreItem(item); setShowRestoreModal(true); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#881337] text-white rounded text-[11px] font-bold hover:bg-[#881337]/90">
                              Undo
                            </button>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <button onClick={() => onEditItem(item)} className="p-1 text-gray-400 hover:text-[#881337] hover:bg-[#881337]/5 rounded">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </button>
                              <button onClick={() => { setDeleteItem(item); setShowDeleteModal(true); }} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                // Wine Table Rows
                items.map((item, index) => (
                  <tr
                    key={item.uuid}
                    className="hover:bg-[#FFF9F3] transition-colors group border-b border-gray-50/50"
                  >
                    {userRole !== "employee" && (
                      <td className="pl-4 pr-2 py-3 align-top text-left min-w-[40px]">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.uuid || item.id)}
                          onChange={() =>
                            handleSelectItem(item.uuid || item.id)
                          }
                          className="rounded border-gray-300 text-[#881337] focus:ring-[#881337] w-3.5 h-3.5 mt-1"
                        />
                      </td>
                    )}
                    {/* Wine (Image + Name) */}
                    <td className="pl-4 pr-2 py-3 align-top text-left min-w-[240px]">
                      <div className="flex items-start gap-3">
                        <div className="h-14 w-12 bg-gray-100 rounded shadow-sm border border-gray-100 flex-shrink-0 overflow-hidden">
                          {item.image_url ? (
                            <img
                              src={
                                item.image_url.startsWith("https")
                                  ? item.image_url
                                  : `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url}`
                              }
                              alt={item.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-gray-300">
                              <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="1.5"
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-[13px] font-bold text-gray-900 leading-tight truncate w-full block">
                            {item.producer_name || ""}
                          </span>
                          <span className="text-[12px] text-gray-500 leading-tight mt-0.5 truncate w-full block">
                            {item.product_name || item.name || "-"}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Vintage */}
                    <td className="pl-4 pr-2 py-3 align-top text-left min-w-[60px]">
                      <span className="text-[13px] font-medium text-gray-900">
                        {item.vintage}
                      </span>
                    </td>
                    {/* Category */}
                    <td className="pl-4 pr-2 py-3 align-top text-left min-w-[100px]">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[12px] font-bold shadow-sm whitespace-nowrap inline-block ${item.category?.toLowerCase() === "red"
                          ? "bg-[#9F1239] text-white"
                          : item.category?.toLowerCase() === "white"
                            ? "bg-[#FEF08A] text-[#854D0E]"
                            : item.category?.toLowerCase() === "sparkling"
                              ? "bg-[#FACC15] text-[#854D0E]"
                              : item.category?.toLowerCase() === "rosé" ||
                                item.category?.toLowerCase() === "rose"
                                ? "bg-[#FCE7F3] text-[#9D174D]"
                                : item.category?.toLowerCase() === "orange"
                                  ? "bg-[#FFEDD5] text-[#9A3412]"
                                  : item.category?.toLowerCase() === "dessert"
                                    ? "bg-[#D97706] text-white"
                                    : "bg-gray-100 text-gray-700"
                          }`}
                      >
                        {item.category || "-"}
                      </span>
                    </td>
                    {/* Restaurant */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[120px]">
                      <div className="flex flex-col min-w-0">
                        <span className="text-[12px] font-semibold text-gray-900 leading-tight truncate">
                          {item.restaurantname || "-"}
                        </span>
                        <span className="text-[12px] text-gray-500">
                          Restaurant
                        </span>
                      </div>
                    </td>
                    {/* Varietals */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[160px]">
                      <div className="flex flex-wrap gap-1">
                        {(item.varietals || item.grape_varietals || []).length >
                          0 ? (
                          (item.varietals || item.grape_varietals)
                            .slice(0, 2)
                            .map((v, i) => (
                              <span
                                key={i}
                                className="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded text-[12px] text-gray-600 font-medium whitespace-nowrap"
                              >
                                {v}
                              </span>
                            ))
                        ) : (
                          <span className="text-[11px] text-gray-400">-</span>
                        )}
                        {(item.varietals || item.grape_varietals || []).length >
                          2 && (
                            <span className="text-[12px] text-gray-400 self-center">
                              +
                              {(item.varietals || item.grape_varietals).length -
                                2}
                            </span>
                          )}
                      </div>
                    </td>
                    {/* Region */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[160px]">
                      <div className="flex flex-col text-[12px] text-gray-700 leading-tight min-w-0">
                        <span className="truncate font-medium">
                          {item.region?.country || ""}
                        </span>
                        {item.region?.region && (
                          <span className="text-gray-500 truncate">
                            / {item.region.region}
                          </span>
                        )}
                        {item.region?.sub_region && (
                          <span className="text-gray-400 text-[12px] truncate">
                            / {item.region.sub_region}
                          </span>
                        )}
                        {item.region?.commune_appellation && (
                          <span className="text-gray-400 text-[12px] truncate">
                            / {item.region.commune_appellation}
                          </span>
                        )}
                        {item.region?.vineyard && (
                          <span className="text-gray-400 text-[12px] truncate">
                            via {item.region.vineyard}
                          </span>
                        )}
                      </div>
                    </td>
                    {/* Style */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[130px]">
                      {item.sub_category ||
                        item.style?.name ||
                        item.styleDisplay ? (
                        <span
                          className={`px-2 py-0.5 rounded text-[12px] font-medium border inline-block ${getStyleBadgeColor(item.category)}`}
                        >
                          {(
                            item.sub_category ||
                            item.style?.name ||
                            item.styleDisplay
                          ).toLowerCase()}
                        </span>
                      ) : (
                        <span className="text-[12px] text-gray-400">-</span>
                      )}
                    </td>
                    {/* Characteristics */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[140px]">
                      <div className="flex flex-wrap gap-1">
                        {(item.is_filtered || item.filtered === "Yes") && (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-200 rounded text-[12px] font-medium">
                            Filtered
                          </span>
                        )}
                        {(item.is_vegan === true || item.vegan === "Yes") && (
                          <span className="px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-100 rounded text-[12px] font-medium">
                            Vegan
                          </span>
                        )}
                        {(item.has_residual_sugar === true ||
                          item.has_residual_sugar === "Yes") && (
                            <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-100 rounded text-[12px] font-medium">
                              Sweet
                            </span>
                          )}
                        {(item.is_organic === true ||
                          item.organic === "Yes") && (
                            <span className="px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-100 rounded text-[12px] font-medium">
                              Organic
                            </span>
                          )}
                        {(item.is_biodynamic === true ||
                          item.biodynamic === "Yes") && (
                            <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-100 rounded text-[12px] font-medium">
                              Bio
                            </span>
                          )}
                      </div>
                    </td>
                    {/* Offering */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[100px]">
                      <div className="flex gap-1">
                        {(item.offering?.by_the_glass ||
                          item.offered_by_glass) && (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[12px] font-bold uppercase tracking-wide">
                              BTG
                            </span>
                          )}
                        {(item.offering?.by_the_bottle ||
                          item.offered_by_bottle) && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[12px] font-bold uppercase tracking-wide">
                              BTB
                            </span>
                          )}
                      </div>
                    </td>
                    {/* Price */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[120px]">
                      <div className="flex flex-col text-[12px] font-bold text-gray-900 gap-0.5">
                        {(item.offering?.glass_price || item.glass_price) && (
                          <span>
                            ${item.offering?.glass_price || item.glass_price}{" "}
                            <span className="text-[12px] text-gray-400 font-normal">
                              / glass
                            </span>
                          </span>
                        )}
                        {(item.offering?.bottle_price || item.bottle_price) && (
                          <span>
                            ${item.offering?.bottle_price || item.bottle_price}{" "}
                            <span className="text-[12px] text-gray-400 font-normal">
                              / bottle
                            </span>
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="pl-4 pr-4 py-3 align-top min-w-[150px]">
                      {item.training_stats?.denominator > 0 ? (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 transition-all duration-500"
                              style={{ width: `${item.training_stats.completion_percentage}%` }}
                            />
                          </div>
                          <span className="text-[12px] font-black text-gray-700">
                            {item.training_stats.completion_percentage}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-[12px] font-bold text-gray-300 mt-1 block">—</span>
                      )}
                    </td>
                    {previousTable && (
                      <td className="px-4 py-2">
                        <span className="text-[12px] font-bold text-gray-900 whitespace-nowrap">
                          {item.onMenuDisplay || "—"}
                        </span>
                      </td>
                    )}
                    {/* Updated */}
                    <td className="pl-4 pr-4 py-3 align-top text-left min-w-[90px]">
                      <span className="text-[12px] font-medium text-gray-500 whitespace-nowrap block mt-0.5">
                        {formatDate(item.updatedAt)}
                      </span>
                    </td>
                    {/* Status */}
                    {userRole !== "employee" && (
                      <td
                        className={`py-3 align-top text-left min-w-[80px] ${userRole === "employee" ? "pl-4 pr-8" : "pl-4 pr-2"
                          }`}
                      >
                        <span
                          className={`px-2 py-0.5 rounded-full text-[12px] font-bold uppercase tracking-wide border inline-block ${item.status
                            ? "bg-green-50 text-green-600 border-green-100"
                            : "bg-gray-100 text-gray-500 border-gray-200"
                            }`}
                        >
                          {item.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                    )}
                    {/* Actions */}
                    {userRole !== "employee" && (
                      <td className="pl-4 pr-2 py-3 align-top text-left min-w-[70px]">
                        <div className="flex items-center gap-1 mt-0.5">
                          {previousTable ? (
                            <button
                              onClick={() => {
                                setRestoreItem(item);
                                setShowRestoreModal(true);
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#881337] text-white rounded-md text-[12px] font-bold hover:bg-[#881337]/90 transition-all"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2.5"
                                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                                />
                              </svg>
                              Undo
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => onEditItem(item)}
                                className="p-1.5 text-gray-400 hover:text-[#881337] hover:bg-[#881337]/5 rounded transition-all"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteItem(item);
                                  setShowDeleteModal(true);
                                }}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
            {loading ? (
              <div className="col-span-full py-20 text-center">
                <div className="flex flex-col items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#881337]/20 border-t-[#881337]"></div>
                  <p className="mt-4 text-sm font-medium text-gray-500">
                    Loading your menu...
                  </p>
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="col-span-full py-20 text-center text-gray-500 font-medium">
                No items found
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.uuid}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow group flex flex-col h-full"
                >
                  {/* Card Header with Image and Badges */}
                  <div className="relative h-48 w-full flex-shrink-0">
                    {activeTab === "beer" ? (
                      <BeerImage
                        src={item.image_url}
                        alt={item.product_name}
                        className="h-full w-full object-cover"
                        fallbackClassName="h-full w-full object-contain"
                      />
                    ) : item.image_url ? (
                      <img
                        src={
                          item.image_url.startsWith("https")
                            ? item.image_url
                            : `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url}`
                        }
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-gray-50 flex items-center justify-center text-gray-300">
                        {activeTab === "wine" ? (
                          <svg
                            className="w-12 h-12"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-12 h-12"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        )}
                      </div>
                    )}

                    {activeTab === "wine" ? (
                      <div className="absolute top-3 right-3 flex gap-2">
                        {item.vintage && (
                          <span className="px-2 py-1 bg-white shadow-sm rounded-md text-[12px] font-bold text-gray-900 border border-gray-100">
                            {item.vintage}
                          </span>
                        )}
                        {item.category && (
                          <span
                            className={`px-2 py-1 shadow-sm rounded-full text-[12px] font-bold text-white border border-white/20 ${item.category.toLowerCase() === "red"
                              ? "bg-[#9F1239]"
                              : item.category.toLowerCase() === "white"
                                ? "bg-[#EAB308]"
                                : item.category.toLowerCase() === "rose"
                                  ? "bg-[#F472B6]"
                                  : "bg-gray-800"
                              }`}
                          >
                            {item.category}
                          </span>
                        )}
                      </div>
                    ) : activeTab === "beer" ? (
                      <div className="absolute top-3 right-3 flex gap-2">
                        {getFermentationFamily(item.style) && (
                          <span className="text-[11px] font-bold text-gray-800 bg-white px-2.5 py-1 rounded-full shadow-sm border border-gray-200 uppercase">
                            {getFermentationFamily(item.style)}
                          </span>
                        )}
                        {getOriginClassification(item.origin?.country) && (
                          <span
                            className={`text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm border uppercase ${getOriginClassification(item.origin?.country) === "DOMESTIC"
                              ? "bg-[#16A34A] text-white border-[#16A34A]"
                              : "bg-amber-500 text-white border-amber-500"
                              }`}
                          >
                            {getOriginClassification(item.origin?.country)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        {item.temperature && (
                          <div className="absolute top-3 left-3 flex gap-2">
                            <span className="px-2 py-0.5 bg-white shadow-sm rounded-full text-[12px] font-bold text-gray-600">
                              {item.temperature}
                            </span>
                          </div>
                        )}
                        <div className="absolute top-3 right-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[12px] font-black uppercase ${item.status
                              ? "bg-green-500 text-white shadow-sm"
                              : "bg-gray-500 text-white shadow-sm"
                              }`}
                          >
                            {item.status ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-3 sm:p-4 flex text-start flex-col flex-grow space-y-3 sm:space-y-4 justify-between min-w-0">
                    <div className="min-w-0">
                      {activeTab === "wine" ? (
                        // WINE CARD LAYOUT
                        <div className="space-y-3 sm:space-y-4 min-w-0">
                          {/* Title Section */}
                          <div className="min-w-0">
                            <h4 className="text-sm sm:text-base font-extrabold text-[#111827] leading-tight break-words">
                              {item.producer_name || "Unknown Producer"}
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5 break-words">
                              {item.product_name || item.name}
                            </p>
                          </div>

                          {/* Details Grid */}
                          <div className="space-y-2 text-[10px] sm:text-[11px] min-w-0">
                            {/* Restaurant */}
                            <div className="flex flex-col sm:grid sm:grid-cols-[90px_1fr] items-start sm:items-center gap-0.5 sm:gap-2">
                              <span className="text-gray-500 font-medium whitespace-nowrap">
                                Restaurant:
                              </span>
                              <span className="text-gray-900 font-semibold break-words min-w-0">
                                {item.restaurantname || "-"}
                              </span>
                            </div>

                            {/* Varietals */}
                            <div className="flex flex-col sm:grid sm:grid-cols-[90px_1fr] items-start sm:items-center gap-1 sm:gap-2">
                              <span className="text-gray-500 font-medium whitespace-nowrap">
                                Varietals:
                              </span>
                              <div className="flex flex-wrap gap-1 min-w-0">
                                {(item.varietals || item.grape_varietals || [])
                                  .slice(0, 3)
                                  .map((v, i) => (
                                    <span
                                      key={i}
                                      className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] sm:text-[12px] font-bold break-words"
                                    >
                                      {v}
                                    </span>
                                  ))}
                              </div>
                            </div>

                            {/* Region */}
                            <div className="flex flex-col sm:grid sm:grid-cols-[90px_1fr] items-start sm:items-start gap-0.5 sm:gap-2">
                              <span className="text-gray-500 font-medium mt-0.5 whitespace-nowrap">
                                Region:
                              </span>
                              <div className="text-gray-700 leading-tight break-words min-w-0">
                                {item.region?.country && (
                                  <span>{item.region.country}</span>
                                )}
                                {item.region?.region && (
                                  <span className="text-gray-400">
                                    {" "}
                                    › {item.region.region}
                                  </span>
                                )}
                                {item.region?.sub_region && (
                                  <span className="text-gray-400">
                                    {" "}
                                    › {item.region.sub_region}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Style */}
                            {(item.sub_category || item.style?.name) && (
                              <div className="flex flex-col sm:grid sm:grid-cols-[90px_1fr] items-start sm:items-center gap-1 sm:gap-2">
                                <span className="text-gray-500 font-medium whitespace-nowrap">
                                  Style:
                                </span>
                                <span className={`px-2 py-0.5 rounded text-[10px] sm:text-[12px] font-bold max-w-full break-words border ${getStyleBadgeColor(item.category)}`}>
                                  {item.sub_category || item.style?.name}
                                </span>
                              </div>
                            )}

                            {/* Body */}
                            {item.style?.body && (
                              <div className="flex flex-col sm:grid sm:grid-cols-[90px_1fr] items-start sm:items-center gap-1 sm:gap-2">
                                <span className="text-gray-500 font-medium whitespace-nowrap">
                                  Body:
                                </span>
                                <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-[10px] sm:text-[12px] font-bold max-w-full break-words">
                                  {item.style.body}
                                </span>
                              </div>
                            )}

                            {/* Texture */}
                            {item.style?.texture && (
                              <div className="flex flex-col sm:grid sm:grid-cols-[90px_1fr] items-start sm:items-center gap-1 sm:gap-2">
                                <span className="text-gray-500 font-medium whitespace-nowrap">
                                  Texture:
                                </span>
                                <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-[10px] sm:text-[12px] font-bold max-w-full break-words">
                                  {item.style.texture}
                                </span>
                              </div>
                            )}

                            {/* Flavor Intensity */}
                            {item.style?.flavor_intensity && (
                              <div className="flex flex-col sm:grid sm:grid-cols-[90px_1fr] items-start sm:items-center gap-1 sm:gap-2">
                                <span className="text-gray-500 font-medium whitespace-nowrap">
                                  Intensity:
                                </span>
                                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] sm:text-[12px] font-bold max-w-full break-words">
                                  {item.style.flavor_intensity}
                                </span>
                              </div>
                            )}
                          </div>

                          <hr className="border-gray-100" />

                          {/* Notes */}
                          {(item.notes || item.chef_notes) && (
                            <div className="flex gap-2 items-start bg-yellow-50/50 p-2 rounded-lg min-w-0">
                              <div className="flex-shrink-0 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-yellow-400 rounded-sm mt-0.5"></div>
                              <div className="min-w-0">
                                <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase block mb-0.5">
                                  Notes
                                </span>
                                <p className="text-[10px] sm:text-[12px] text-gray-700 italic leading-relaxed break-words">
                                  {item.notes || item.chef_notes}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Pricing */}
                          <div className="space-y-2 pt-1 min-w-0">
                            {(item.offering?.by_the_glass ||
                              item.offered_by_glass) && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] sm:text-[12px] font-bold uppercase w-10 text-center flex-shrink-0">
                                    BTG
                                  </span>
                                  <span className="text-base sm:text-lg font-black text-gray-900">
                                    $
                                    {item.offering?.glass_price || item.glass_price}
                                  </span>
                                  <span className="text-[10px] sm:text-[12px] text-gray-400 font-medium">
                                    / glass
                                  </span>
                                </div>
                              )}
                            {(item.offering?.by_the_bottle ||
                              item.offered_by_bottle) && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] sm:text-[12px] font-bold uppercase w-10 text-center flex-shrink-0">
                                    BTB
                                  </span>
                                  <span className="text-base sm:text-lg font-black text-gray-900">
                                    $
                                    {item.offering?.bottle_price ||
                                      item.bottle_price}
                                  </span>
                                  <span className="text-[10px] sm:text-[12px] text-gray-400 font-medium">
                                    / bottle
                                  </span>
                                </div>
                              )}
                          </div>

                          {/* Characteristics Pills */}
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {(item.is_filtered || item.filtered === "Yes") && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] sm:text-[12px] font-bold">
                                Filtered
                              </span>
                            )}
                            {(item.is_vegan === true || item.vegan === "Yes") && (
                              <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-[10px] sm:text-[12px] font-bold">
                                Vegan
                              </span>
                            )}
                            {(item.is_organic === true ||
                              item.organic === "Yes") && (
                                <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-[10px] sm:text-[12px] font-bold">
                                  Organic
                                </span>
                              )}
                          </div>
                        </div>
                      ) : activeTab === "beer" ? (
                        // BEER CARD LAYOUT
                        <div className="flex flex-col flex-grow min-w-0">
                          {/* Title Section */}
                          <div className="mb-4">
                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide break-words">
                              {item.restaurantname || "Unknown Restaurant"}
                            </p>
                            <p className="text-sm font-medium text-gray-500 mt-0.5 break-words">
                              {item.brewery_name || "Unknown Brewery"}
                            </p>
                            <h2 className="text-[20px] font-black text-gray-900 leading-tight break-words">
                              {item.product_name || item.name}
                            </h2>
                          </div>

                          {/* Origin Hierarchy */}
                          <div className="text-[12px] pb-4 border-b border-gray-100 min-w-0">
                            <div className="text-gray-400 font-bold uppercase tracking-wide text-[10px] mb-1">Origin</div>
                            {item.origin?.country ? (
                              <>
                                <div className="text-gray-900 font-bold break-words">{item.origin.country}</div>
                                {item.origin?.state && (
                                  <div className="text-gray-700 pl-3 flex items-center gap-1 break-words">
                                    <span className="text-gray-400">↳</span>{item.origin.state}
                                  </div>
                                )}
                                {item.origin?.city && (
                                  <div className="text-gray-700 pl-6 flex items-center gap-1 break-words">
                                    <span className="text-gray-400">↳</span>{item.origin.city}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-gray-400">-</div>
                            )}
                          </div>

                          {/* Classification */}
                          <div className="text-[11px] py-4 border-b border-gray-100 min-w-0">
                            <div className="text-gray-400 font-bold uppercase tracking-wide text-[10px] mb-2">Classification</div>
                            <div className="grid grid-cols-[72px_1fr] gap-y-2 gap-x-2 min-w-0 items-center">
                              <div className="text-gray-500 font-medium">Style Group:</div>
                              <div className="flex justify-start min-w-0">
                                {item.category ? <span className={`border rounded-full px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${getBeerCategoryColor(item.category)}`}>{item.category}</span> : "-"}
                              </div>

                              <div className="text-gray-500 font-medium">Core Style:</div>
                              <div className="flex justify-start min-w-0">
                                {item.style ? <span className={`border rounded-full px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${getBeerCategoryColor(item.category)}`}>{item.style}</span> : "-"}
                              </div>

                              <div className="text-gray-500 font-medium">Body:</div>
                              <div className="flex justify-start min-w-0">
                                {item.body ? <span className="bg-[#F1F5F9] text-[#1E293B] border border-gray-200 rounded-md px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap">{item.body}</span> : "-"}
                              </div>

                              <div className="text-gray-500 font-medium">Texture:</div>
                              <div className="flex justify-start min-w-0">
                                {item.texture ? <span className="bg-blue-50 text-blue-700 border border-blue-200 rounded-md px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap">{item.texture}</span> : "-"}
                              </div>

                              <div className="text-gray-500 font-medium">Flavor<br />Intensity:</div>
                              <div className="flex justify-start min-w-0">
                                {item.intensity ? <span className="bg-purple-50 text-purple-700 border border-purple-200 rounded-md px-2 py-0.5 text-[11px] font-semibold break-words">{item.intensity}</span> : "-"}
                              </div>
                            </div>
                          </div>

                          {/* ABV/IBU */}
                          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 py-4 border-b border-gray-100">
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-[11px] font-bold text-gray-400">ABV</span>
                              <span className={`text-[11px] font-black border px-1.5 py-0.5 rounded whitespace-nowrap ${getBeerLevelColor(getAbvCategory(item.abv))}`}>{getAbvCategory(item.abv) || "-"}</span>
                              <span className="text-[11px] font-black border border-gray-200 bg-white px-1.5 py-0.5 rounded text-gray-900 whitespace-nowrap">{item.abv ?? "0"}%</span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-[11px] font-bold text-gray-400">IBU</span>
                              <span className={`text-[11px] font-black border px-1.5 py-0.5 rounded whitespace-nowrap ${getBeerLevelColor(getIbuCategory(item.ibu))}`}>{getIbuCategory(item.ibu) || "-"}</span>
                              <span className="text-[11px] font-black border border-gray-200 bg-white px-1.5 py-0.5 rounded text-gray-900 whitespace-nowrap">{item.ibu ?? "0"}</span>
                            </div>
                          </div>

                          {/* Notes */}
                          {item.notes && (
                            <div className="bg-[#FEFCE8] border border-[#FEF08A] rounded-lg p-3 my-4">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-3 h-3 bg-[#EAB308] rounded-sm"></div>
                                <span className="text-[11px] font-bold text-gray-500 tracking-wider">NOTES</span>
                              </div>
                              <p className="text-[12px] text-gray-600 italic leading-relaxed">
                                {item.notes}
                              </p>
                            </div>
                          )}

                          {/* Pricing Matrix */}
                          <div className="space-y-3 my-4">
                            {renderBeerFormatRows(item.formats?.find(f => f.format === 'draft'), 'DRAFT', 'bg-[#DBEAFE] text-[#1D4ED8]')}
                            {renderBeerFormatRows(item.formats?.find(f => f.format === 'can'), 'CAN', 'bg-[#E5E7EB] text-[#374151]')}
                            {renderBeerFormatRows(item.formats?.find(f => f.format === 'bottle'), 'BOTTLE', 'bg-[#F3F4F6] text-[#4B5563]')}
                          </div>

                          {/* Attributes */}
                          <div className="mb-6">
                            <div className="text-gray-400 font-bold uppercase tracking-wide text-[10px] mb-2">Attributes</div>
                            <div className="flex flex-wrap gap-2 text-[11px] font-bold">
                              {item.has_gluten === false && (
                                <div className="flex items-center gap-1.5 text-[#15803D] bg-[#ECFDF5] px-2.5 py-1 rounded-full">
                                  Gluten-Free
                                </div>
                              )}
                              {item.is_vegan && (
                                <div className="flex items-center gap-1.5 text-[#15803D] bg-[#ECFDF5] px-2.5 py-1 rounded-full">
                                  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6.375 1.50176C4.53281 1.50176 2.97422 2.7088 2.44453 4.37286C3.23203 3.97442 4.12031 3.75176 5.0625 3.75176H7.125C7.33125 3.75176 7.5 3.92051 7.5 4.12676C7.5 4.33301 7.33125 4.50176 7.125 4.50176H6.75H5.0625C4.67344 4.50176 4.29609 4.5463 3.93281 4.62833C3.32578 4.76661 2.76094 5.0127 2.25938 5.34786C0.897656 6.25489 0 7.80411 0 9.56426V9.93926C0 10.251 0.250781 10.5018 0.5625 10.5018C0.874219 10.5018 1.125 10.251 1.125 9.93926V9.56426C1.125 8.42286 1.61016 7.3963 2.38594 6.67676C2.85 8.4463 4.46016 9.75176 6.375 9.75176H6.39844C9.49453 9.73536 12 6.6838 12 2.92208C12 1.92364 11.8242 0.974421 11.5055 0.118952C11.4445 -0.0427669 11.2078 -0.0357356 11.1258 0.116608C10.6852 0.941608 9.81328 1.50176 8.8125 1.50176H6.375Z" fill="#15803D" />
                                  </svg>
                                  Vegan
                                </div>
                              )}
                              {item.has_lactose && (
                                <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full">
                                  Contains Lactose
                                </div>
                              )}
                              {item.is_non_alcoholic && (
                                <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full">
                                  Non-Alcoholic
                                </div>
                              )}
                              {item.has_nuts && (
                                <div className="flex items-center gap-1.5 text-red-600 bg-red-50 px-2.5 py-1 rounded-full">
                                  Contains Nuts
                                </div>
                              )}
                              {item.is_filtered && (
                                <div className="flex items-center gap-1.5 text-gray-700 bg-gray-100 px-2.5 py-1 rounded-full">
                                  Filtered
                                </div>
                              )}
                              {item.seasonal && (
                                <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
                                  Seasonal
                                </div>
                              )}
                              {item.limited && (
                                <div className="flex items-center gap-1.5 text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full">
                                  Limited Release
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Footer Info (Status, Updated) */}
                          <div className="mt-auto pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-1.5 text-gray-400 text-[11px] font-medium">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Updated {formatDate(item.updatedAt)}
                              </div>
                              <span className={`text-[10px] font-black tracking-wider ${item.status ? 'text-[#059669]' : 'text-gray-500'}`}>
                                {item.status ? "ACTIVE" : "INACTIVE"}
                              </span>
                            </div>

                            {userRole !== "employee" && (
                              <div className="flex gap-2">
                                {previousTable ? (
                                  <button
                                    onClick={() => {
                                      setRestoreItem(item);
                                      setShowRestoreModal(true);
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 bg-[#111827] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-black transition-all"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                    Undo
                                  </button>
                                ) : (
                                  <>
                                    <button onClick={() => onEditItem(item)} className="flex-1 flex items-center justify-center gap-2 bg-[#111827] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-black transition-all">
                                      <svg stroke="currentColor" fill="none" viewBox="0 0 24 24" className="w-4 h-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                      Edit
                                    </button>
                                    <button onClick={() => { setDeleteItem(item); setShowDeleteModal(true); }} className="w-11 h-11 flex items-center justify-center bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex-shrink-0">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        // FOOD CARD LAYOUT (Existing)
                        <div className="space-y-3 sm:space-y-4 min-w-0">
                          <div className="space-y-1 min-w-0">
                            <div className="flex items-center gap-1.5 text-[10px] sm:text-[12px] text-gray-400 font-semibold min-w-0">
                              <svg
                                className="w-3 h-3 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                              <span className="truncate">
                                {item.restaurantNameDisplay ||
                                  item.restaurantname ||
                                  "Unknown Restaurant"}
                              </span>
                            </div>
                            <h3 className="text-sm sm:text-base font-black text-gray-900 leading-tight break-words">
                              {item.name || item.product_name}
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] sm:text-[12px] font-bold text-orange-600 px-2 py-0.5 bg-orange-50 rounded-lg whitespace-nowrap">
                                {item.typeDisplay || item.category || "General"}
                              </span>
                              <span className="text-[10px] sm:text-[12px] font-black text-purple-600 px-2 py-0.5 bg-purple-50 rounded-lg whitespace-nowrap">
                                ${item.price?.toFixed(2) || "0.00"}
                              </span>
                            </div>
                            {item.description && (
                              <p className="text-[11px] sm:text-[12px] text-gray-600 font-medium leading-snug mt-1 italic break-words">
                                {item.description}
                              </p>
                            )}
                          </div>

                          {/* Tags Section */}
                          <div className="space-y-3 min-w-0">
                            {/* Allergens */}
                            <div className="min-w-0">
                              <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                                Allergens
                              </span>
                              <div className="flex flex-wrap gap-1 min-w-0">
                                {item.allergens?.length > 0 ? (
                                  item.allergens.map((alg, i) => (
                                    <span
                                      key={i}
                                      className="px-2 py-0.5 bg-red-50 text-red-600 rounded text-[10px] sm:text-[12px] font-bold border border-red-100 capitalize break-words"
                                    >
                                      {alg}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-[10px] sm:text-[12px] text-gray-300 italic">
                                    None
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Health, Belief & Lifestyle Row */}
                            <div className="grid grid-cols-1 gap-3 min-w-0">
                              <div className="min-w-0">
                                <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                                  Health Restrictions
                                </span>
                                <div className="flex flex-wrap gap-1 min-w-0">
                                  {item.dietary_restrictions?.health?.length >
                                    0 ? (
                                    item.dietary_restrictions.health.map(
                                      (h, i) => (
                                        <span
                                          key={i}
                                          className={`px-2 py-0.5 rounded text-[10px] sm:text-[12px] font-bold border capitalize break-words ${getTagStyle(
                                            "health",
                                            h
                                          )}`}
                                        >
                                          {h}
                                        </span>
                                      )
                                    )
                                  ) : (
                                    <span className="text-[10px] sm:text-[12px] text-gray-300 italic">
                                      None
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="grid grid-cols-1 gap-3 min-w-0">
                                <div className="min-w-0">
                                  <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                                    Belief Accommodations
                                  </span>
                                  <div className="flex flex-wrap gap-1 min-w-0">
                                    {item.dietary_restrictions?.belief?.length >
                                      0 ? (
                                      item.dietary_restrictions.belief.map(
                                        (b, i) => (
                                          <span
                                            key={i}
                                            className={`px-2 py-0.5 rounded text-[10px] sm:text-[12px] font-bold border capitalize break-words ${getTagStyle(
                                              "belief",
                                              b
                                            )}`}
                                          >
                                            {b}
                                          </span>
                                        )
                                      )
                                    ) : (
                                      <span className="text-[10px] sm:text-[12px] text-gray-300 italic">
                                        None
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="min-w-0">
                                  <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                                    Lifestyle Accommodations
                                  </span>
                                  <div className="flex flex-wrap gap-1 min-w-0">
                                    {item.dietary_restrictions?.lifestyle
                                      ?.length > 0 ? (
                                      item.dietary_restrictions.lifestyle.map(
                                        (l, i) => (
                                          <span
                                            key={i}
                                            className={`px-2 py-0.5 rounded text-[10px] sm:text-[12px] font-bold border capitalize break-words ${getTagStyle(
                                              "lifestyle",
                                              l
                                            )}`}
                                          >
                                            {l}
                                          </span>
                                        )
                                      )
                                    ) : (
                                      <span className="text-[10px] sm:text-[12px] text-gray-300 italic">
                                        None
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Accommodations */}
                            <div className="min-w-0">
                              <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 min-w-0">
                                Accommodations (Inherent)
                              </span>
                              <div className="flex flex-wrap gap-1 min-w-0">
                                {item.accommodations?.length > 0 ? (
                                  item.accommodations.map((acc, i) => (
                                    <span
                                      key={i}
                                      className="px-2 py-0.5 bg-yellow-50 text-yellow-700 rounded text-[10px] sm:text-[12px] font-bold border border-yellow-100 capitalize break-words"
                                    >
                                      {acc}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-[10px] sm:text-[12px] text-gray-300 italic">
                                    None
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Details with Toggle/Button Style */}
                          <div className="space-y-2 border-t border-gray-50 pt-3 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button className="flex items-center gap-1.5 px-3 py-1 bg-gray-50 text-gray-700 rounded-lg text-[10px] sm:text-[12px] font-bold hover:bg-gray-100 transition-colors whitespace-nowrap">
                                <svg
                                  className="w-3.5 h-3.5 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                                Ingredients
                              </button>
                            </div>
                            <p className="text-[11px] sm:text-[12px] text-gray-600 font-medium leading-relaxed italic break-words">
                              {Array.isArray(item.ingredients)
                                ? item.ingredients.join(", ")
                                : item.ingredients || "No ingredients listed."}
                            </p>

                            <div className="flex flex-col sm:flex-row sm:items-center justify-between py-1 gap-2">
                              <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50/50 text-orange-700 rounded-lg text-[10px] sm:text-[12px] font-bold w-fit">
                                <svg
                                  className="w-3.5 h-3.5 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                                  />
                                </svg>
                                Substitutions
                              </div>
                              <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 whitespace-nowrap">
                                Available:{" "}
                                <span
                                  className={
                                    (item.can_substitute === true || item.can_substitute === "Yes")
                                      ? "text-green-600"
                                      : "text-gray-500"
                                  }
                                >
                                  {(item.can_substitute === true || item.can_substitute === "Yes") ? "Yes" : "No"}
                                </span>
                              </span>
                            </div>
                            {(item.can_substitute === true || item.can_substitute === "Yes") && (
                              <div className="mt-1 ml-2 space-y-1 min-w-0">
                                {item.substitutions?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 min-w-0">
                                    {item.substitutions.map((sub, i) => (
                                      <span
                                        key={i}
                                        className="px-1.5 py-0.5 bg-gray-50 text-gray-600 border border-gray-100 rounded text-[9px] sm:text-[10px] font-medium break-words"
                                      >
                                        {sub}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {item.substitution_notes && (
                                  <p className="text-[10px] sm:text-[11px] text-gray-500 italic break-words">
                                    Note: {item.substitution_notes}
                                  </p>
                                )}
                              </div>
                            )}

                            <div className="flex items-center gap-1.5 px-2 py-1 bg-red-50/50 text-red-700 rounded-lg text-[10px] sm:text-[12px] font-bold w-fit mt-1">
                              <svg
                                className="w-3.5 h-3.5 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                              </svg>
                              Cross Contact
                            </div>
                            <span className="text-[10px] sm:text-[12px] font-bold text-gray-600 ml-2 mt-0.5 block break-words">
                              Cross Contact:{" "}
                              <span
                                className={
                                  item.is_cross_contact
                                    ? "text-red-600"
                                    : "text-gray-500"
                                }
                              >
                                {item.is_cross_contact ? "Yes" : "No"}
                              </span>
                            </span>

                            <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50/50 text-blue-700 rounded-lg text-[10px] sm:text-[12px] font-bold w-fit mt-1">
                              <svg
                                className="w-3.5 h-3.5 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                />
                              </svg>
                              Notes
                            </div>
                            <p className="text-[10px] sm:text-[12px] text-gray-400 italic ml-2 mt-0.5 break-words">
                              {item.notes ||
                                "No special notes provided for this dish."}
                            </p>
                          </div>
                        </div>
                      )}{" "}
                      {/* Progress with Footer */}
                      {/* Footer Info (Status, Updated, Training) - Replaces progress bar style */}
                      {/* The beer card renders its own footer and actions above. */}
                      {activeTab !== "beer" && (
                      <div className="pt-4 mt-auto border-t border-gray-50 flex items-center justify-between gap-2">
                        <div className="flex flex-col gap-1 min-w-0">
                          <div className="flex items-center gap-1.5 text-gray-900 font-bold text-[10px] sm:text-[12px] min-w-0">
                            <svg
                              className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                              />
                            </svg>
                            <span className="text-gray-400 font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                              Updated {formatDate(item.updatedAt)}
                            </span>
                          </div>
                        </div>

                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] sm:text-[12px] font-black uppercase whitespace-nowrap ${item.status
                            ? "bg-green-50 text-green-600 border border-green-100"
                            : "bg-gray-100 text-gray-500 border border-gray-200"
                            }`}
                        >
                          {item.status ? "Active" : "Inactive"}
                        </span>
                      </div>
                      )}
                    </div>

                    {/* Card Actions Footer */}
                    {userRole !== "employee" && activeTab !== "beer" && (
                      <div className="flex gap-2 pt-2 px-3 sm:px-4 pb-4 mt-auto">
                        {previousTable ? (
                          <button
                            onClick={() => {
                              setRestoreItem(item);
                              setShowRestoreModal(true);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 bg-[#881337] text-white py-2.5 rounded-xl text-[10px] sm:text-xs font-black shadow-lg shadow-[#881337]/20 hover:bg-[#881337]/90 transition-all active:scale-95"
                          >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                            Undo
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => onEditItem(item)}
                              className="flex-1 min-w-0 flex items-center justify-center gap-2 bg-[#111827] text-white py-2 sm:py-2.5 rounded-lg text-[10px] sm:text-xs font-black shadow-lg shadow-black/10 hover:bg-black transition-all active:scale-95 px-2"
                            >
                              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              <span className="truncate">Edit</span>
                            </button>
                            <button
                              onClick={() => {
                                setDeleteItem(item);
                                setShowDeleteModal(true);
                              }}
                              className="flex-shrink-0 p-2 sm:p-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 border border-red-100 transition-colors active:scale-95"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {/* Pagination Section */}
      <div className="px-4 sm:px-8 py-5 bg-white flex flex-col items-center justify-between border-t border-[#FDE6D2] gap-4 sm:flex-row">
        <div className="text-[12px] sm:text-xs font-bold text-gray-500 text-center sm:text-left">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
          results
        </div>

        <div className="flex items-center gap-2">
          <button
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            className="flex items-center text-[12px] sm:text-xs font-bold text-gray-400 hover:text-[#881337] disabled:text-gray-200 transition-colors px-2 sm:px-3 py-1"
          >
            Prev
          </button>

          <div className="flex items-center gap-1.5">
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => !previousTable && onPageChange(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-black transition-all ${currentPage === page
                      ? "bg-[#881337] text-white shadow-md shadow-[#881337]/20"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-100"
                      }`}
                  >
                    {page}
                  </button>
                );
              }
              if (page === currentPage - 2 || page === currentPage + 2) {
                return (
                  <span key={page} className="text-gray-400 px-1">
                    ...
                  </span>
                );
              }
              return null;
            })}
          </div>

          <button
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            className="flex items-center text-[12px] sm:text-xs font-bold text-gray-400 hover:text-[#881337] disabled:text-gray-200 transition-colors px-2 sm:px-3 py-1"
          >
            Next
          </button>
        </div>
      </div>
      {/* Menu Insights Analytics Dashboard */}
      {
        !previousTable && activeTab === "wine" && winePanelStats && (
          <WineDashboardStats {...winePanelStats} />
        )
      }
      {
        !previousTable && activeTab === "beer" && beerPanelStats && (
          <BeerDashboardStats
            {...beerPanelStats}
            BeerActivityData={beerActivityData}
            onScroll={handleBeerActivityScroll}
            BeerActivityTotalCount={beerActivityTotalCount}
            BeerActivityLoading={beerActivityLoading}
            userRole={userRole}
          />
        )
      }
      {
        !previousTable && (
          <div
            className={`mt-8 px-2 pb-12 space-y-8 text-start ${activeTab === "wine" || activeTab === "beer" ? "hidden" : ""
              }`}
          >
            {/* Top Stats Cards */}
            {/** Use API analytics when available, otherwise fall back to local computed stats */}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-2">
              {/* Total Dishes */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 flex items-center justify-between shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <div className="flex flex-col gap-1">
                  <span className="text-[14px] font-medium text-gray-500">
                    Total Dishes
                  </span>
                  <span className="text-3xl font-black text-gray-900 leading-tight">
                    {dishAnalytics?.totalDishes ?? localDishStats.total}
                  </span>
                  <span className="text-[12px] font-bold text-[#10B981] flex items-center gap-1 mt-1">
                    {dishAnalytics?.newDishesInThirtyDays
                      ? `↑ +${dishAnalytics.newDishesInThirtyDays} this month`
                      : "↑ +0 this month"}
                  </span>
                </div>
                <div className="w-14 h-14 bg-[#FEF3C7] rounded-xl flex items-center justify-center">
                  <svg
                    className="w-7 h-7 text-[#92140C]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" />
                  </svg>
                </div>
              </div>

              {/* Active Dishes */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 flex items-center justify-between shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <div className="flex flex-col gap-1">
                  <span className="text-[14px] font-medium text-gray-500">
                    Active Dishes
                  </span>
                  <span className="text-3xl font-black text-gray-900 leading-tight">
                    {dishAnalytics?.activeDishesCount ??
                      localDishStats.activeCount}
                  </span>
                  <span className="text-[12px] font-bold text-[#10B981] flex items-center gap-1 mt-1">
                    {dishAnalytics?.activeRate
                      ? `↑ ${Number(dishAnalytics.activeRate).toFixed(
                        1
                      )}% active rate`
                      : `↑ ${localDishStats.activeRate}% active rate`}
                  </span>
                  <span className="text-[12px] text-gray-500 mt-1">
                    {dishAnalytics
                      ? `${dishAnalytics.newDishesInThirtyDays || 0} new · ${dishAnalytics.removedDishesInThirtyDays || 0
                      } removed (30d)`
                      : ""}
                  </span>
                  {dishAnalytics?.lastUpdatedDish && (
                    <span className="text-[11px] text-gray-400 mt-0.5">
                      Last updated: {dishAnalytics.lastUpdatedDish}
                    </span>
                  )}
                </div>
                <div className="w-14 h-14 bg-[#DCFCE7] rounded-xl flex items-center justify-center">
                  <svg
                    className="w-7 h-7 text-[#22C55E]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
              </div>

              {/* Recent Updates */}
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 flex items-center justify-between shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <div className="flex flex-col gap-1">
                  <span className="text-[14px] font-medium text-gray-500">
                    Recent Updates
                  </span>
                  <span className="text-3xl font-black text-gray-900 leading-tight">
                    {dishAnalytics?.recentDishUpdates
                      ? dishAnalytics.recentDishUpdates.filter(
                        (u) =>
                          Date.now() - new Date(u.createdAt).getTime() <=
                          7 * 24 * 60 * 60 * 1000
                      ).length
                      : localDishStats.recentCount}
                  </span>
                  <span className="text-[12px] font-bold text-gray-400 flex items-center gap-1 mt-1">
                    Last 7 Days
                  </span>
                </div>
                <div className="w-14 h-14 bg-[#F3E8FF] rounded-xl flex items-center justify-center">
                  <svg
                    className="w-7 h-7 text-[#9333EA]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category Distribution */}
              <div className="lg:col-span-1 text-start bg-white p-4 sm:p-6 lg:p-8 rounded-2xl border border-gray-100 shadow-sm">
                <h3 className="text-sm font-black text-gray-900 mb-8 tracking-tight">
                  Category Distribution
                </h3>
                <div className="space-y-4">
                  {(() => {
                    const distribution = dishAnalytics?.dishTypesWithPercentages
                      ? dishAnalytics.dishTypesWithPercentages
                      : [
                        { label: "Appetizer", count: 0, percentage: 0 },
                        { label: "Main", count: 0, percentage: 0 },
                        { label: "Dessert", count: 0, percentage: 0 },
                        { label: "Pizza", count: 0, percentage: 0 },
                        { label: "Pasta", count: 0, percentage: 0 },
                        { label: "Sandwich/Wrap", count: 0, percentage: 0 },
                        { label: "Salad", count: 0, percentage: 0 },
                        { label: "Soup", count: 0, percentage: 0 },
                        { label: "Entrée", count: 0, percentage: 0 },
                        { label: "Special", count: 0, percentage: 0 },
                        { label: "Side", count: 0, percentage: 0 },
                      ];

                    return distribution.map((category, idx) => {
                      const label =
                        category.type || category.label || category.name;
                      const value = Number(
                        category.percentage ?? category.value ?? 0
                      );
                      const count = category.count ?? 0;
                      const colors = [
                        "#881337",
                        "#F97316",
                        "#EAB308",
                        "#F43F5E",
                        "#B45309",
                        "#8B5CF6",
                        "#10B981",
                        "#EA580C",
                        "#3B82F6",
                        "#EC4899",
                        "#D97706",
                      ];
                      const color = category.color || colors[idx % colors.length];
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between gap-6"
                        >
                          <div className="flex items-center gap-3 w-32">
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: color }}
                            ></div>
                            <span className="text-[13px] font-bold text-gray-500 truncate">
                              {label}
                            </span>
                          </div>
                          <div className="flex-1 flex items-center gap-6">
                            <div className="flex-1 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${value.toFixed(1)}%`,
                                  backgroundColor: color,
                                }}
                              ></div>
                            </div>
                            <span className="text-[13px] font-black text-gray-900 w-6 text-right">
                              {count}
                            </span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>

            {/* Dietary & Allergen Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Dietary Accommodations Section */}
              <div className="bg-white text-start p-4 sm:p-6 lg:p-8 rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md h-full">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></div>
                    <h3 className="text-[17px] font-black text-gray-900 tracking-tight">
                      Dietary Accommodations
                    </h3>
                  </div>
                  <p className="text-[12px] font-medium text-gray-400">
                    Support for dietary preferences.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(() => {
                    const total =
                      dishAnalytics?.totalDishes ??
                      totalItems ??
                      items.length ??
                      1;
                    const vegan = dishAnalytics?.veganDishes ?? 0;
                    const vegetarian = dishAnalytics?.vegetarianDishes ?? 0;
                    const gf = dishAnalytics?.gluttenFreeDishes ?? 0; // note: API typo maintained
                    const nutFree = dishAnalytics?.nuttFreeDishes ?? 0;
                    const dairyFree =
                      dishAnalytics?.dairyFreeDishes ??
                      Math.max(0, total - (dishAnalytics?.milkDishes ?? 0));
                    const soyFree =
                      dishAnalytics?.soyFreeDishes ??
                      Math.max(0, total - (dishAnalytics?.soyDishes ?? 0));

                    const arr = [
                      {
                        label: "Vegan",
                        count: vegan,
                        percent: `${((vegan / total) * 100).toFixed(1)}%`,
                        icon: "🌱",
                        bg: "#F0FDF4",
                      },
                      {
                        label: "Vegetarian",
                        count: vegetarian,
                        percent: `${((vegetarian / total) * 100).toFixed(1)}%`,
                        icon: "🥗",
                        bg: "#F0FDF4",
                      },
                      {
                        label: "Gluten-Free",
                        count: gf,
                        percent: `${((gf / total) * 100).toFixed(1)}%`,
                        icon: "🌾",
                        bg: "#FFFBEB",
                      },
                      {
                        label: "Dairy-Free",
                        count: dairyFree,
                        percent: `${((dairyFree / total) * 100).toFixed(1)}%`,
                        icon: "🥛",
                        bg: "#F0F9FF",
                      },
                      {
                        label: "Nut-Free",
                        count: nutFree,
                        percent: `${((nutFree / total) * 100).toFixed(1)}%`,
                        icon: "🥜",
                        bg: "#FFF7ED",
                      },
                      {
                        label: "Soy-Free",
                        count: soyFree,
                        percent: `${((soyFree / total) * 100).toFixed(1)}%`,
                        icon: "🫘",
                        bg: "#FEF2F2",
                      },
                    ];
                    return arr;
                  })().map((item, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-xl flex items-center gap-3 transition-all hover:scale-[1.02] cursor-default group"
                      style={{ backgroundColor: item.bg }}
                    >
                      <span className="text-xl group-hover:rotate-12 transition-transform">
                        {item.icon}
                      </span>
                      <div className="flex flex-col">
                        <span className="text-[12px] font-black text-gray-500 uppercase tracking-tighter leading-none">
                          {item.label}
                        </span>
                        <div className="flex items-baseline gap-2 mt-0.5">
                          <span className="text-[15px] font-black text-gray-900 leading-none">
                            {item.count}
                          </span>
                          <span className="text-[12px] font-bold text-gray-400 leading-none">
                            {item.percent}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Allergen Breakdown Section */}
              <div className="bg-white text-start p-4 sm:p-6 lg:p-8 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden transition-all hover:shadow-md h-full">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#EF4444]"></div>
                      <h3 className="text-[17px] font-black text-gray-900 tracking-tight">
                        Allergen Breakdown
                      </h3>
                    </div>
                    <p className="text-[12px] font-medium text-gray-400">
                      Prevalence of key allergens.
                    </p>
                  </div>
                  {/* <div className="bg-[#FFFBEB] border border-[#FEF3C7] px-3 py-1.5 rounded-xl flex items-center gap-2 shadow-sm">
                <div className="w-5 h-5 bg-[#FEF3C7] rounded flex items-center justify-center text-[#92400E]">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-[11px] font-black text-[#881337]">Wheat (45.7%)</span>
              </div> */}
                </div>

                <div className="grid grid-cols-1 gap-y-2">
                  {(dishAnalytics
                    ? [
                      {
                        label: "Milk",
                        count: dishAnalytics?.milkDishes || 0,
                        icon: "🥛",
                      },
                      {
                        label: "Eggs",
                        count: dishAnalytics?.eggDishes || 0,
                        icon: "🍳",
                      },
                      {
                        label: "Fish",
                        count: dishAnalytics?.fishDishes || 0,
                        icon: "🐟",
                      },
                      {
                        label: "Shellfish",
                        count: dishAnalytics?.shellfishDishes || 0,
                        icon: "🦀",
                      },
                      {
                        label: "Tree Nuts",
                        count: dishAnalytics?.treeNutDishes || 0,
                        icon: "🌰",
                      },
                      {
                        label: "Wheat",
                        count: dishAnalytics?.wheatDishes || 0,
                        icon: "🌾",
                        active: true,
                      },
                      {
                        label: "Soy",
                        count: dishAnalytics?.soyDishes || 0,
                        icon: "🫘",
                      },
                    ]
                    : [
                      {
                        label: "Milk",
                        count: 42,
                        percent: "33.1%",
                        icon: "🥛",
                      },
                      {
                        label: "Eggs",
                        count: 28,
                        percent: "22.0%",
                        icon: "🍳",
                      },
                      {
                        label: "Fish",
                        count: 15,
                        percent: "11.8%",
                        icon: "🐟",
                      },
                      {
                        label: "Shellfish",
                        count: 8,
                        percent: "6.3%",
                        icon: "🦀",
                      },
                      {
                        label: "Tree Nuts",
                        count: 12,
                        percent: "9.4%",
                        icon: "🌰",
                      },
                      {
                        label: "Wheat",
                        count: 58,
                        percent: "45.7%",
                        icon: "🌾",
                        active: true,
                      },
                      { label: "Soy", count: 18, percent: "14.2%", icon: "🫘" },
                    ]
                  ).map((allergen, idx) => {
                    const total = dishAnalytics?.totalDishes || totalItems || 1;
                    const percent = dishAnalytics
                      ? `${((allergen.count / total) * 100).toFixed(1)}%`
                      : allergen.percent;
                    return (
                      <div
                        key={idx}
                        className={`p-2.5 rounded-xl flex items-center justify-between border border-transparent transition-all group ${allergen.active
                          ? "bg-[#FFF9F3] border-[#FDE6D2]"
                          : "bg-gray-50/50 hover:bg-white hover:border-gray-200"
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg group-hover:scale-110 transition-transform">
                            {allergen.icon}
                          </span>
                          <div className="flex flex-col">
                            <span
                              className={`text-[12px] font-black ${allergen.active
                                ? "text-[#881337]"
                                : "text-gray-900"
                                }`}
                            >
                              {allergen.label}
                            </span>
                            <span className="text-[12px] font-bold text-gray-400 uppercase leading-none">
                              {allergen.count} dishes
                            </span>
                          </div>
                        </div>
                        <span
                          className={`text-[12px] font-black ${allergen.active ? "text-[#881337]" : "text-gray-900"
                            }`}
                        >
                          {percent}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* Recent Activity Section */}
            {userRole !== "employee" && (
              <div className="bg-white p-4 sm:p-6 lg:p-8 text-start rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md w-full max-w-[90rem]">
                <h3 className="text-[17px] font-black text-gray-900 tracking-tight mb-6">
                  Recent Activity
                </h3>

                {/* fixed height + scrollable list */}
                <div className="space-y-2 h-[850px] overflow-y-auto pr-2">
                  {(dishAnalytics?.recentDishUpdates &&
                    dishAnalytics.recentDishUpdates.length
                    ? dishAnalytics.recentDishUpdates
                    : [
                      {
                        action: "dish_created",
                        details: { description: "New dish added" },
                        createdAt: new Date(),
                        user_uuid: null,
                      },
                    ]
                  ).map((u, idx) => {
                    const action = u.action || "update";
                    const text =
                      u.details?.description ||
                      (action === "dish_created"
                        ? "New dish added"
                        : "Menu item updated");
                    const user = u.user_uuid;
                    const subtitle = user
                      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                      : "";
                    const hours = Math.floor(
                      (Date.now() - new Date(u.createdAt).getTime()) /
                      (1000 * 60 * 60)
                    );
                    const time =
                      hours < 24
                        ? `${hours} hrs ago`
                        : `${Math.floor(hours / 24)} days ago`;

                    const { icon, bg } = (function () {
                      if (action === "dish_created")
                        return {
                          icon: (
                            <svg
                              className="w-5 h-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2.5"
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          ),
                          bg: "bg-green-50",
                        };
                      if (action === "dish_archived")
                        return {
                          icon: (
                            <svg
                              className="w-5 h-5 text-red-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          ),
                          bg: "bg-red-50",
                        };
                      return {
                        icon: (
                          <svg
                            className="w-5 h-5 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        ),
                        bg: "bg-blue-50",
                      };
                    })();

                    return (
                      <div
                        key={idx}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border border-gray-50 hover:border-[#FDE6D2] hover:bg-[#FFF9F3]/30 transition-all group gap-3"
                      >
                        <div className="flex items-center gap-4 sm:gap-5">
                          <div
                            className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform`}
                          >
                            {icon}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-[13px] sm:text-[14px] font-black text-gray-900 leading-tight truncate">
                              {text}
                            </span>
                            {subtitle && (
                              <span className="text-[12px] font-medium text-gray-500 mt-1 line-clamp-1">{`By ${subtitle}`}</span>
                            )}
                          </div>
                        </div>
                        <span className="text-[12px] font-bold text-gray-400 whitespace-nowrap self-end sm:self-center">
                          {time}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )
      }
      {/* Delete Modal */}
      {
        showDeleteModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[60] backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Archive Item
              </h2>
              <p className="text-sm text-gray-600 mb-6 font-medium">
                Are you sure you want to move this{" "}
                {activeTab === "beer" ? "beer" : deleteItem?.category ? "wine" : "dish"} to your previous menu?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  onClick={() => handleDelete(deleteItem)}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    "Archive"
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Restore Modal */}
      {
        showRestoreModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[60] backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Restore Item
              </h2>
              <p className="text-sm text-gray-600 mb-6 font-medium">
                Are you sure you want to restore this{" "}
                {activeTab === "beer" ? "beer" : restoreItem?.category ? "wine" : "dish"} and move it back to your
                active menu?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setShowRestoreModal(false)}
                  disabled={restoreLoading}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2 bg-[#881337] text-white text-sm font-bold rounded-lg hover:bg-[#881337]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                  onClick={() => handleRestore(restoreItem)}
                  disabled={restoreLoading}
                >
                  {restoreLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    "Restore"
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Bulk Restore Modal for pervious menu */}
      {
        showBulkRestoreModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[60] backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Restore to Current Menu
              </h2>
              <p className="text-sm text-gray-600 mb-6 font-medium">
                You are about to restore {selectedItems.length}{" "}
                {activeTab === "food" ? "dish" : "wine"}
                {selectedItems.length !== 1 ? "es" : ""} to the current menu.
                These items will become active immediately. Do you want to
                continue?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                  onClick={() => setShowBulkRestoreModal(false)}
                  disabled={bulkRestoreLoading}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2 bg-[#881337] text-white text-sm font-bold rounded-lg hover:bg-[#881337]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                  onClick={handleBulkRestore}
                  disabled={bulkRestoreLoading}
                >
                  {bulkRestoreLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Bulk Restore as Draft Modal for previous menu */}
      {
        showBulkRestoreDraftModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[60] backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Restore as Draft
              </h2>
              <p className="text-sm text-gray-600 mb-6 font-medium">
                You are about to restore {selectedItems.length}{" "}
                {activeTab === "food" ? "dish" : activeTab === "beer" ? "beer" : "wine"}
                {selectedItems.length !== 1 ? (activeTab === "beer" ? "s" : "es") : ""} as drafts. These items
                will not be live until activated. Do you want to continue?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                  onClick={() => setShowBulkRestoreDraftModal(false)}
                  disabled={bulkRestoreDraftLoading}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2 bg-[#881337] text-white text-sm font-bold rounded-lg hover:bg-[#881337]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                  onClick={handleBulkRestoreAsDraft}
                  disabled={bulkRestoreDraftLoading}
                >
                  {bulkRestoreDraftLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Food Bulk Actions Modal (active menu management) */}
      {
        showFoodBulkModal && (
          <FoodBulkActionModal
            isOpen={showFoodBulkModal}
            onClose={() => setShowFoodBulkModal(false)}
            onConfirm={handleFoodBulkConfirm}
            selectedCount={selectedItems.length}
            validDishTypes={validDishTypes}
          />
        )
      }
      {/* Wine Bulk Actions Modal (active menu management) */}
      {
        showWineBulkModal && (
          <WineBulkActionModal
            isOpen={showWineBulkModal}
            onClose={() => setShowWineBulkModal(false)}
            onConfirm={handleWineBulkConfirm}
            selectedCount={selectedItems.length}
          />
        )
      }
      {/* Beer Bulk Actions Modal (current menu actions, or restore on the previous menu) */}
      {
        showBeerBulkModal && (
          <BeerBulkActionModal
            isOpen={showBeerBulkModal}
            onClose={() => setShowBeerBulkModal(false)}
            onConfirm={handleBeerBulkConfirm}
            selectedCount={selectedItems.length}
            selectedBeers={items
              .filter((i) => selectedItems.includes(i.uuid || i.id))
              .map((i) => ({
                uuid: i.uuid || i.id,
                product_name: i.product_name || i.name,
                brewery_name: i.brewery_name,
              }))}
            previousTable={previousTable}
          />
        )
      }
      {/* Wine Reference Sheet Modal */}
      {
        showWineReferenceSheet && (
          <WineReferenceSheet
            isOpen={showWineReferenceSheet}
            onClose={() => setShowWineReferenceSheet(false)}
            items={items}
            selectedItems={selectedItems}
            onFetchAllItems={onFetchAllItems}
          />
        )
      }
      {/* Beer Reference Sheet Modal */}
      {
        showBeerReferenceSheet && (
          <BeerReferenceSheet
            isOpen={showBeerReferenceSheet}
            onClose={() => setShowBeerReferenceSheet(false)}
            items={items}
            selectedItems={selectedItems}
            onFetchAllItems={onFetchAllItems}
          />
        )
      }
    </div >
  );
}
